import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Search,
  Filter,
  Mail,
  Phone,
  MapPin,
  Calendar,
  CheckCircle2,
  Clock,
  MoreVertical,
  Plus,
  ChevronDown,
  MessageSquare,
  UserPlus,
  Settings,
  Star,
  X,
  Upload,
  Building2,
  Briefcase,
  User,
  Code,
  Check,
  Copy,
  CheckCheck,
  Shield,
  ToggleLeft,
  ToggleRight,
  AlertCircle,
  Lock,
  Package2,
  RefreshCw,
  Edit2,
  Globe,
  Flag,
  Hash,
  UserCog,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  Zap,
  Crown,
  Activity,
  Database,
  Boxes,
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { notifyProfileUpdate, notifyUserFollowed } from '../../lib/notificationService';
import { toast } from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';

const DEPARTMENTS = [
  { id: 'all', label: 'Tüm Departmanlar', icon: Users },
  { id: 'development', label: 'Yazılım Geliştirme', icon: Code },
  { id: 'design', label: 'Tasarım', icon: Briefcase },
  { id: 'marketing', label: 'Pazarlama', icon: Building2 },
  { id: 'management', label: 'Yönetim', icon: Users },
];

const ROLES = [
  { id: 'all', label: 'Tüm Roller', icon: Users },
  { id: 'team_lead', label: 'Takım Lideri', icon: Users },
  { id: 'senior', label: 'Kıdemli Geliştirici', icon: Code },
  { id: 'developer', label: 'Geliştirici', icon: Code },
  { id: 'designer', label: 'Tasarımcı', icon: Briefcase },
  { id: 'manager', label: 'Yönetici', icon: Users },
];

const SORT_OPTIONS = [
  { id: 'name_asc', label: 'İsim (A-Z)', icon: ChevronDown },
  { id: 'name_desc', label: 'İsim (Z-A)', icon: ChevronDown },
  { id: 'date_asc', label: 'Katılım (Eski-Yeni)', icon: Calendar },
  { id: 'date_desc', label: 'Katılım (Yeni-Eski)', icon: Calendar },
  { id: 'tasks_asc', label: 'Görev Sayısı (Az-Çok)', icon: CheckCircle2 },
  { id: 'tasks_desc', label: 'Görev Sayısı (Çok-Az)', icon: CheckCircle2 },
];

const STATUS_OPTIONS = [
  { id: 'all', label: 'Tüm Durumlar', color: 'gray' },
  { id: 'online', label: 'Çevrimiçi', color: 'green' },
  { id: 'offline', label: 'Çevrimdışı', color: 'gray' },
];

const Team = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name_asc');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBio, setEditingBio] = useState(false);
  const [editingSkills, setEditingSkills] = useState(false);
  const [tempBio, setTempBio] = useState('');
  const [tempSkills, setTempSkills] = useState('');
  const [skillInput, setSkillInput] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [customDepartment, setCustomDepartment] = useState('');
  const [customRole, setCustomRole] = useState('');
  const [showCustomDepartment, setShowCustomDepartment] = useState(false);
  const [showCustomRole, setShowCustomRole] = useState(false);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [showProfilePhoto, setShowProfilePhoto] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false); // Track if current user is admin
  const [userRole, setUserRole] = useState(null);
  // Dinamik departman ve rol listeleri için state'ler
  const [departmentsList, setDepartmentsList] = useState([
    { id: 'all', label: 'Tüm Departmanlar', icon: Users }
  ]);
  const [rolesList, setRolesList] = useState([
    { id: 'all', label: 'Tüm Roller', icon: Users },
    { id: 'user', label: 'Yetkisiz Kişi', icon: User },
    { id: 'admin', label: 'Admin Yetkili Kişi', icon: Shield },
    { id: 'super', label: 'Süper Yönetici', icon: Shield }
  ]);
  // Add state for showing profile details modal
  const [showProfileDetails, setShowProfileDetails] = useState(false);
  const [profileMember, setProfileMember] = useState(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(3); // 3x3 grid
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Add these state variables to the main Team component
  const [editedBios, setEditedBios] = useState({});
  const [editedSkills, setEditedSkills] = useState({});

  const navigate = useNavigate();

  const isNew = (startDate) => {
    const threeDaysAgo = new Date();
    threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
    return new Date(startDate) > threeDaysAgo;
  };

  useEffect(() => {
    getCurrentUser();
    fetchDepartmentsAndRoles();

    // Realtime subscriptions
    const userProfilesSubscription = supabase
      .channel('user_profiles_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_profiles'
        },
        (payload) => {
          // console.log('Realtime update:', payload);
          
          switch (payload.eventType) {
            case 'INSERT':
              setTeamMembers(prev => [transformMemberData(payload.new), ...prev]);
              break;
            case 'UPDATE':
              setTeamMembers(prev =>
                prev.map(member =>
                  member.id === payload.new.id ? transformMemberData(payload.new) : member
                )
              );
              break;
            case 'DELETE':
              setTeamMembers(prev =>
                prev.filter(member => member.id !== payload.old.id)
              );
              break;
          }
        }
      )
      .subscribe();

    const followsSubscription = supabase
      .channel('follows_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'follows'
        },
        (payload) => {
          // console.log('Follows update:', payload);
          if (selectedMember) {
            fetchFollowData(selectedMember.id);
          }
          
          // Takip durumu değiştiğinde tüm listeyi güncelle
          if (currentUserId) {
            fetchTeamMembers();
          }
        }
      )
      .subscribe();

    // Cleanup subscriptions
    return () => {
      userProfilesSubscription.unsubscribe();
      followsSubscription.unsubscribe();
    };
  }, []);
  
  // currentUserId ayarlandıktan sonra ekip üyelerini yükle
  useEffect(() => {
    if (currentUserId) {
      // console.log('CurrentUserId güncellendi, ekip üyeleri yükleniyor:', currentUserId);
      fetchTeamMembers();
    }
  }, [currentUserId]);

  // currentUserId ayarlandıktan sonra ekip üyeleri yüklenmeden önce rol bilgisini alalım
  useEffect(() => {
    if (currentUserId) {
      const fetchUserRole = async () => {
        try {
          const { data, error } = await supabase
            .from('user_profiles')
            .select('role')
            .eq('id', currentUserId)
            .single();
          
          if (!error && data) {
            setUserRole(data.role);
            // Admin kontrolünü de burada yapalım
            setIsAdmin(data.role === 'admin' || data.role === 'super');
          }
        } catch (error) {
          console.error('Kullanıcı rolü alınırken hata:', error);
        }
      };
      
      fetchUserRole();
      // Sonra ekip üyelerini yükle
      fetchTeamMembers();
    }
  }, [currentUserId]);

  // Helper function to transform member data
  const transformMemberData = (member) => ({
    id: member.id,
    name: `${member.first_name} ${member.last_name}`,
    role: member.role || 'user', // System role (super, admin, user)
    department: member.job_position || 'Belirtilmemiş',
    job_position: member.job_position || 'Belirtilmemiş',
    avatar: member.profile_photo_url || `https://ui-avatars.com/api/?name=${member.first_name}+${member.last_name}&background=3b82f6&color=fff`,
    email: member.email,
    phone: member.phone,
    location: `${member.city || 'Belirtilmemiş'}, ${member.country || 'Belirtilmemiş'}`,
    startDate: member.created_at,
    status: member.online_status ? 'online' : 'offline',
    skills: member.skills ? member.skills.split(',').map(skill => skill.trim()) : [],
    bio: member.biography || 'Henüz biyografi eklenmemiş.',
  });

  const fetchTeamMembers = async () => {
    try {
      // Kullanıcı profillerini getir
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*, is_takip_permission, stok_takip_permission, role')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Tüm görevleri bir kerede getir
      const { data: tasksData, error: tasksError } = await supabase
        .from('tasks')
        .select('*');
      
      if (tasksError) throw tasksError;

      // Mevcut kullanıcının takip ettiği tüm kişileri bir kerede getir
      const { data: userFollowing, error: followError } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', currentUserId);
      
      if (followError) throw followError;
      
      // Takip edilen kişilerin ID'lerini bir set olarak tut
      const followingSet = new Set(userFollowing.map(f => f.following_id));

      // Her üye için takipçi ve takip edilen sayılarını getir
      const membersWithFollowData = await Promise.all(data.map(async (member) => {
        // Takipçi sayısını getir
        const { data: followers, error: followersError } = await supabase
          .from('follows')
          .select('follower_id')
          .eq('following_id', member.id);

        if (followersError) throw followersError;

        // Takip edilen sayısını getir
        const { data: following, error: followingError } = await supabase
          .from('follows')
          .select('following_id')
          .eq('follower_id', member.id);

        if (followingError) throw followingError;

        // Mevcut kullanıcının bu üyeyi takip edip etmediğini kontrol et
        // Önceden getirdiğimiz takip set'ini kullan
        const isFollowing = followingSet.has(member.id);

        // Takipçi ve takip edilen profilleri getir
        const { data: followerProfiles, error: followerProfilesError } = await supabase
          .from('user_profiles')
          .select('id, first_name, last_name, profile_photo_url')
          .in('id', followers?.map(f => f.follower_id) || []);

        if (followerProfilesError) throw followerProfilesError;

        const { data: followingProfiles, error: followingProfilesError } = await supabase
          .from('user_profiles')
          .select('id, first_name, last_name, profile_photo_url')
          .in('id', following?.map(f => f.following_id) || []);

        if (followingProfilesError) throw followingProfilesError;

        // Üyeye atanan görevleri hesapla
        // assigned_members alanı JSON dizisi olarak saklanır, içinde id'ler bulunur
        const memberTasks = tasksData.filter(task => {
          if (!task.assigned_members) return false;
          
          try {
            // assigned_members bir string olarak saklanmışsa JSON'a çevir
            const assignedMembers = typeof task.assigned_members === 'string' 
              ? JSON.parse(task.assigned_members) 
              : task.assigned_members;
              
            // Üyenin ID'si assigned_members içinde var mı kontrol et
            return Array.isArray(assignedMembers) && assignedMembers.some(m => 
              (typeof m === 'object' && m.id === member.id) || m === member.id
            );
          } catch (e) {
            console.error("JSON parse error:", e);
            return false;
          }
        });
        
        // Aktif görevler (todo ve in_progress durumunda olanlar)
        const activeTasks = memberTasks.filter(task => 
          task.status === 'todo' || task.status === 'in_progress'
        ).length;
        
        // Tamamlanan görevler
        const completedTasks = memberTasks.filter(task => 
          task.status === 'done'
        ).length;

        return {
          ...transformMemberData(member),
          activeTasks,
          completedTasks,
          followers: followerProfiles?.map(profile => ({
            id: profile.id,
            name: `${profile.first_name} ${profile.last_name}`,
            avatar: profile.profile_photo_url || `https://ui-avatars.com/api/?name=${profile.first_name}+${profile.last_name}&background=3b82f6&color=fff`
          })) || [],
          following: followingProfiles?.map(profile => ({
            id: profile.id,
            name: `${profile.first_name} ${profile.last_name}`,
            avatar: profile.profile_photo_url || `https://ui-avatars.com/api/?name=${profile.first_name}+${profile.last_name}&background=3b82f6&color=fff`
          })) || [],
          isFollowing: isFollowing,
          is_takip_permission: member.is_takip_permission !== null ? member.is_takip_permission : true, // Default to true if null
          stok_takip_permission: member.stok_takip_permission !== null ? member.stok_takip_permission : true, // Default to true if null
        };
      }));

      setTeamMembers(membersWithFollowData);
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentUser = async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;
      setCurrentUserId(user?.id);
      
      // Get user profile to check if user is admin
      if (user) {
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', user.id)
          .single();
          
        if (!profileError && profile) {
          // Check if user has admin role (you may need to adjust this based on your role structure)
          setIsAdmin(profile.role === 'admin' || profile.role === 'super');
        }
      }
    } catch (error) {
      console.error('Error getting current user:', error);
    }
  };

  const fetchFollowData = async (userId) => {
    try {
      // Takipçileri getir
      const { data: followersData, error: followersError } = await supabase
        .from('follows')
        .select('follower_id')
        .eq('following_id', userId);

      if (followersError) throw followersError;

      // Takip edilenleri getir
      const { data: followingData, error: followingError } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', userId);

      if (followingError) throw followingError;

      // Takip durumunu kontrol et
      const isUserFollowing = followersData.some(f => f.follower_id === currentUserId);
      setIsFollowing(isUserFollowing);
      
      // Eğer seçili bir üye varsa ve bu onun verisiyse, selectedMember'ı da güncelle
      if (selectedMember && selectedMember.id === userId) {
        setSelectedMember(prev => ({
          ...prev,
          isFollowing: isUserFollowing
        }));
      }

      // Takipçi ve takip edilen profilleri getir
      const followerIds = followersData.map(f => f.follower_id);
      const followingIds = followingData.map(f => f.following_id);

      const { data: followerProfiles, error: followerProfilesError } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, profile_photo_url')
        .in('id', followerIds);

      if (followerProfilesError) throw followerProfilesError;

      const { data: followingProfiles, error: followingProfilesError } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, profile_photo_url')
        .in('id', followingIds);

      if (followingProfilesError) throw followingProfilesError;

      setFollowers(followerProfiles.map(profile => ({
        id: profile.id,
        name: `${profile.first_name} ${profile.last_name}`,
        avatar: profile.profile_photo_url || `https://ui-avatars.com/api/?name=${profile.first_name}+${profile.last_name}&background=3b82f6&color=fff`
      })));

      setFollowing(followingProfiles.map(profile => ({
        id: profile.id,
        name: `${profile.first_name} ${profile.last_name}`,
        avatar: profile.profile_photo_url || `https://ui-avatars.com/api/?name=${profile.first_name}+${profile.last_name}&background=3b82f6&color=fff`
      })));
    } catch (error) {
      console.error('Error fetching follow data:', error);
    }
  };

  const handleFollow = async (memberId) => {
    try {
      const member = teamMembers.find(m => m.id === memberId);
      if (!member) return;

      // Mevcut kullanıcının bilgilerini al
      const { data: currentUserProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, profile_photo_url')
        .eq('id', currentUserId)
        .single();

      if (profileError) throw profileError;

      // Mevcut takip durumu
      const currentFollowingState = member.isFollowing;
      // console.log(`Takip işlemi öncesi durum: ${member.name} - ${currentFollowingState ? 'Takip ediliyor' : 'Takip edilmiyor'}`);

      if (currentFollowingState) {
        // Unfollow işlemleri...
        const { error } = await supabase
          .from('follows')
          .delete()
          .eq('follower_id', currentUserId)
          .eq('following_id', memberId);

        if (error) throw error;

        // Update local state
        setTeamMembers(prev => prev.map(m => 
          m.id === memberId 
            ? { 
                ...m, 
                isFollowing: false,
                followers: m.followers.filter(f => f.id !== currentUserId)
              }
            : m
        ));

        // Seçili üye detaylarını güncelle
        if (selectedMember?.id === memberId) {
          setIsFollowing(false);
          setSelectedMember(prev => ({
            ...prev,
            isFollowing: false
          }));
        }

        // Başarılı mesajı göster
        toast.success('Takipten çıkıldı');

      } else {
        // Takip edilip edilmediğini kontrol et
        const { data: existingFollow, error: checkError } = await supabase
          .from('follows')
          .select('*')
          .eq('follower_id', currentUserId)
          .eq('following_id', memberId)
          .maybeSingle();
        
        if (checkError) throw checkError;
        
        // Eğer zaten takip ediliyorsa, sadece UI durumunu güncelle
        if (existingFollow) {
          // console.log('Bu kişi zaten takip ediliyor, UI durumu güncelleniyor');
          
          // Update local state to reflect the correct following state
          setTeamMembers(prev => prev.map(m => 
            m.id === memberId ? { ...m, isFollowing: true } : m
          ));
          
          // Seçili üye detaylarını güncelle
          if (selectedMember?.id === memberId) {
            setIsFollowing(true);
            setSelectedMember(prev => ({
              ...prev,
              isFollowing: true
            }));
          }
          
          toast.info('Bu kişiyi zaten takip ediyorsunuz');
          return;
        }
        
        // Follow işlemleri...
        const { error } = await supabase
          .from('follows')
          .insert([
            {
              follower_id: currentUserId,
              following_id: memberId
            }
          ]);

        if (error) {
          // Benzersizlik hatası ise özel mesaj göster
          if (error.code === '23505') {
            // console.log('Benzersizlik hatası, bu kişi zaten takip ediliyor olabilir.');
            toast.info('Bu kişiyi zaten takip ediyorsunuz');
            
            // UI durumunu güncelle
            setTeamMembers(prev => prev.map(m => 
              m.id === memberId ? { ...m, isFollowing: true } : m
            ));
            
            if (selectedMember?.id === memberId) {
              setIsFollowing(true);
              setSelectedMember(prev => ({
                ...prev,
                isFollowing: true
              }));
            }
            
            return;
          }
          throw error;
        }

        // Update local state
        setTeamMembers(prev => prev.map(m => 
          m.id === memberId 
            ? { 
                ...m, 
                isFollowing: true,
                followers: [...m.followers, {
                  id: currentUserProfile.id,
                  name: `${currentUserProfile.first_name} ${currentUserProfile.last_name}`,
                  avatar: currentUserProfile.profile_photo_url || `https://ui-avatars.com/api/?name=${currentUserProfile.first_name}+${currentUserProfile.last_name}&background=3b82f6&color=fff`
                }]
              }
            : m
        ));

        // Seçili üye detaylarını güncelle
        if (selectedMember?.id === memberId) {
          setIsFollowing(true);
          setSelectedMember(prev => ({
            ...prev,
            isFollowing: true
          }));
        }

        // Takip edilen kişiye bildirim gönder
        await notifyUserFollowed(
          currentUserId,
          `${currentUserProfile.first_name} ${currentUserProfile.last_name}`,
          memberId
        );
        
        // Başarılı mesajı göster
        toast.success('Takip edilmeye başlandı');
      }

      // console.log(`Takip işlemi sonrası durum: ${member.name} - ${!currentFollowingState ? 'Takip ediliyor' : 'Takip edilmiyor'}`);

    } catch (error) {
      console.error('Error following/unfollowing:', error);
      
      // Daha anlaşılır hata mesajları
      if (error.code === '23505') {
        toast.info('Bu kişiyi zaten takip ediyorsunuz');
      } else {
        toast.error('Takip işlemi sırasında bir hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
      }
    }
  };

  // Refresh function
  const handleRefresh = async () => {
    setIsRefreshing(true);
    setCurrentPage(1); // Reset to first page
    await fetchTeamMembers();
    setTimeout(() => setIsRefreshing(false), 500); // Add slight delay for visual feedback
  };

  // Filter and sort logic
  const filteredMembers = teamMembers
    .filter(member => {
      const matchesSearch = member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           member.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           member.job_position.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesDepartment = departmentFilter === 'all' || member.department === departmentFilter;
      const matchesRole = roleFilter === 'all' || member.role === roleFilter;
      const matchesStatus = statusFilter === 'all' || member.status === statusFilter;
      
      return matchesSearch && matchesDepartment && matchesRole && matchesStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name_asc':
          return a.name.localeCompare(b.name);
        case 'name_desc':
          return b.name.localeCompare(a.name);
        case 'date_asc':
          return new Date(a.startDate) - new Date(b.startDate);
        case 'date_desc':
          return new Date(b.startDate) - new Date(a.startDate);
        case 'tasks_asc':
          return a.activeTasks - b.activeTasks;
        case 'tasks_desc':
          return b.activeTasks - a.activeTasks;
        default:
          return 0;
      }
    });

  // Pagination logic
  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMembers = filteredMembers.slice(startIndex, startIndex + itemsPerPage);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, departmentFilter, roleFilter, statusFilter, sortBy]);

  const handleSaveBio = async (memberId, newBio) => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ biography: newBio })
        .eq('id', memberId);

      if (error) throw error;

      // Get user's name for notification
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('first_name, last_name')
        .eq('id', memberId)
        .single();

      if (profileError) throw profileError;

      // Get current user for tracking who made the edit
      const { data: editorProfile, error: editorError } = await supabase
        .from('user_profiles')
        .select('first_name, last_name, role')
        .eq('id', currentUserId)
        .single();
        
      if (editorError) throw editorError;

      const userName = `${userProfile.first_name} ${userProfile.last_name}`;
      const editorName = `${editorProfile.first_name} ${editorProfile.last_name}`;
      const isSuperEdit = editorProfile.role === 'super' && currentUserId !== memberId;

      // Send notification to other users
      await notifyProfileUpdate(
        memberId, 
        userName, 
        'bio',
        isSuperEdit ? `Süper yönetici ${editorName} tarafından düzenlendi` : undefined
      );

      // Update local state
      setTeamMembers(members =>
        members.map(member =>
          member.id === memberId ? { ...member, bio: newBio } : member
        )
      );
      setEditingBio(false);
      
      // Clear stored edits after successful save
      setEditedBios(prev => {
        const newEdits = {...prev};
        delete newEdits[memberId];
        return newEdits;
      });
      
      toast.success(isSuperEdit 
        ? `${userName}'in biyografisi başarıyla güncellendi`
        : 'Biyografi başarıyla güncellendi'
      );
    } catch (error) {
      console.error('Error updating biography:', error);
      toast.error('Biyografi güncellenirken bir hata oluştu');
    }
  };

  const handleSaveSkills = async (memberId, skillsArray) => {
    try {
      const skillsString = skillsArray.join(',');
      const { error } = await supabase
        .from('user_profiles')
        .update({ skills: skillsString })
        .eq('id', memberId);

      if (error) throw error;

      // Get user's name for notification
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('first_name, last_name')
        .eq('id', memberId)
        .single();

      if (profileError) throw profileError;

      // Get current user for tracking who made the edit
      const { data: editorProfile, error: editorError } = await supabase
        .from('user_profiles')
        .select('first_name, last_name, role')
        .eq('id', currentUserId)
        .single();
        
      if (editorError) throw editorError;

      const userName = `${userProfile.first_name} ${userProfile.last_name}`;
      const editorName = `${editorProfile.first_name} ${editorProfile.last_name}`;
      const isSuperEdit = editorProfile.role === 'super' && currentUserId !== memberId;

      // Send notification to other users with edit info
      await notifyProfileUpdate(
        memberId, 
        userName, 
        'skills',
        isSuperEdit ? `Süper yönetici ${editorName} tarafından düzenlendi` : undefined
      );

      // Update local state
      setTeamMembers(members =>
        members.map(member =>
          member.id === memberId ? { ...member, skills: skillsArray } : member
        )
      );
      setEditingSkills(false);
      
      // Clear stored edits after successful save
      setEditedSkills(prev => {
        const newEdits = {...prev};
        delete newEdits[memberId];
        return newEdits;
      });
      
      toast.success(isSuperEdit
        ? `${userName}'in yetenekleri başarıyla güncellendi`
        : 'Yetenekler başarıyla güncellendi'
      );
    } catch (error) {
      console.error('Error updating skills:', error);
      toast.error('Yetenekler güncellenirken bir hata oluştu');
    }
  };

  const handleAddSkill = (currentSkills) => {
    if (skillInput.trim()) {
      const newSkills = [...currentSkills, skillInput.trim()];
      setTempSkills(newSkills.join(','));
      setSkillInput('');
    }
  };

  const handleRemoveSkill = (skillToRemove, currentSkills) => {
    const newSkills = currentSkills.filter(skill => skill !== skillToRemove);
    setTempSkills(newSkills.join(','));
  };

  const handleCopyClick = async (text, field) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        // Modern API yöntemi
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback yöntemi
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
        } catch (err) {
          console.error('Kopyalama başarısız (fallback):', err);
          return;
        } finally {
          textArea.remove();
        }
      }
      // Başarılı kopyalama
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Kopyalama başarısız:', err);
    }
  };

  const MemberCard = ({ member, userRole }) => {
    const isSelected = selectedMember?.id === member.id;
    const isCurrentUser = currentUserId === member.id;
    const [localBio, setLocalBio] = useState(editedBios[member.id] || member.bio);
    const [localSkills, setLocalSkills] = useState(editedSkills[member.id] || member.skills.join(','));
    const [localSkillInput, setLocalSkillInput] = useState('');
    const [isExpanded, setIsExpanded] = useState(false);
    const [showProfilePhoto, setShowProfilePhoto] = useState(false);
    const [currentUserRole, setCurrentUserRole] = useState(null);

    useEffect(() => {
      // Only reset if we don't have edited values stored
      if (!editedBios[member.id]) {
        setLocalBio(member.bio);
      }
      if (!editedSkills[member.id]) {
        setLocalSkills(member.skills.join(','));
      }
    }, [member, editedBios, editedSkills]);
      
    // Separate effect to handle role fetching just once
    useEffect(() => {
      // Get current user role
      const fetchCurrentUserRole = async () => {
        try {
          const { data, error } = await supabase
            .from('user_profiles')
            .select('role')
            .eq('id', currentUserId)
            .single();
          
          if (!error && data) {
            setCurrentUserRole(data.role);
          }
        } catch (error) {
          console.error('Error fetching user role:', error);
        }
      };
      
      if (currentUserId) {
        fetchCurrentUserRole();
      }
    }, [currentUserId]);

    // Seçilen ekip üyesi değiştiğinde takip durumunu güncelle
    useEffect(() => {
      if (isSelected) {
        setIsFollowing(member.isFollowing);
        // console.log('Modal için takip durumu güncellendi:', member.isFollowing);
      }
    }, [isSelected, member.isFollowing]);

    const startEditingBio = () => {
      // Allow editing if current user or if super user
      if (!isCurrentUser && userRole !== 'super') {
        toast.error('Bu işlemi sadece kendi profiliniz veya süper yönetici yetkisine sahipseniz yapabilirsiniz!');
        return;
      }
      setEditingBio(true);
    };

    const startEditingSkills = () => {
      // Allow editing if current user or if super user
      if (!isCurrentUser && userRole !== 'super') {
        toast.error('Bu işlemi sadece kendi profiliniz veya süper yönetici yetkisine sahipseniz yapabilirsiniz!');
        return;
      }
      setEditingSkills(true);
      setLocalSkillInput('');
    };

    // Add the missing handleLocalBioChange function
    const handleLocalBioChange = (value) => {
      setLocalBio(value);
      setEditedBios(prev => ({...prev, [member.id]: value}));
    };

    // Yeni yetenek ekleme fonksiyonu - eksik olan fonksiyon
    const handleLocalAddSkill = () => {
      if (localSkillInput.trim()) {
        // Mevcut yetenekleri virgülle ayrılmış metinden diziye çevir
        const currentSkillsArray = localSkills.split(',').filter(Boolean);
        // Yeni yeteneği ekleyip tekrar string'e çevir
        const newSkills = [...currentSkillsArray, localSkillInput.trim()];
        const newSkillsString = newSkills.join(',');
        setLocalSkills(newSkillsString);
        setEditedSkills(prev => ({...prev, [member.id]: newSkillsString}));
        // Input alanını temizle
        setLocalSkillInput('');
      }
    };

    // Yerel yetenek silme fonksiyonu
    const handleLocalRemoveSkill = (skillToRemove) => {
      const currentSkillsArray = localSkills.split(',').filter(Boolean);
      const newSkills = currentSkillsArray.filter(skill => skill !== skillToRemove);
      const newSkillsString = newSkills.join(',');
      setLocalSkills(newSkillsString);
      setEditedSkills(prev => ({...prev, [member.id]: newSkillsString}));
    };

    // Kapalı kart görünümü
    const ClosedCard = () => (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation(); // Prevent event bubbling
          // Only set selectedMember if not already selected to avoid toggle
          if (!isSelected) {
            // Önce member bilgilerini tazeleyelim
            const refreshMember = async () => {
              try {
                const { data, error } = await supabase
                  .from('user_profiles')
                  .select('*, is_takip_permission, stok_takip_permission, role')
                  .eq('id', member.id)
                  .single();
                  
                if (error) {
                  console.error("Üye bilgileri yüklenemedi:", error);
                  return;
                }
                
                // Ekipler state'ini güncellemeden önce güncel bilgileri alıyoruz
                const updatedMember = {
                  ...member,
                  is_takip_permission: data.is_takip_permission,
                  stok_takip_permission: data.stok_takip_permission,
                  role: data.role
                };
                
                // Güncel bilgilerle selectedMember'ı ayarlıyoruz
                setSelectedMember(updatedMember);
              } catch (err) {
                console.error("Üye bilgileri güncellenirken hata:", err);
                setSelectedMember(member); // Hata durumunda mevcut bilgilerle devam et
              }
            };
            
            refreshMember();
          
            // Takipçi ve takip edilen verilerini getir
            if (currentUserId) {
              fetchFollowData(member.id);
            }
          }
          
          // Set following status directly from member data
          setIsFollowing(member.isFollowing);
        }}
        className="group relative bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-xl border border-gray-100 dark:border-gray-700 hover:border-blue-200 dark:hover:border-blue-600 transition-all duration-300 p-6 text-left w-full h-[520px] flex flex-col hover:scale-[1.02] hover:-translate-y-1"
      >
        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        <div className="relative z-10 flex flex-col h-full">
          {/* Header Section - Fixed height */}
          <div className="flex items-start gap-4 mb-4 h-[100px] flex-shrink-0">
            <div className="relative flex-shrink-0">
              <div className="relative">
                <img
                  src={member.avatar}
                  alt={member.name}
                  className="w-14 h-14 rounded-xl ring-2 ring-gray-200 dark:ring-gray-600 group-hover:ring-blue-300 dark:group-hover:ring-blue-500 transition-all duration-300 object-cover"
                />
                {/* Status indicator */}
                <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white dark:border-gray-800 ${
                  member.status === 'online' 
                    ? 'bg-green-500' 
                    : 'bg-gray-400'
                }`} />
              </div>
              
              {/* New badge */}
              {isNew(member.startDate) && (
                <div className="absolute -top-1 -right-1 px-1.5 py-0.5 bg-red-500 text-white text-xs font-semibold rounded-full">
                  YENİ
                </div>
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              {/* Name */}
              <h3 className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300 truncate mb-2">
                {member.name}
                {member.id === currentUserId && (
                  <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full border border-blue-200 dark:border-blue-700">
                    Siz
                  </span>
                )}
              </h3>
              
              {/* Role badge */}
              <div className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium mb-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                {(() => {
                  if (member.role === 'super') return 'Süper Yönetici';
                  if (member.role === 'admin') return 'Yönetici';
                  if (member.role === 'user') return 'Kullanıcı';
                  return member.role;
                })()}
              </div>
              
              {/* Job position */}
              <p className="text-sm text-gray-600 dark:text-gray-300 truncate mb-1">
                {member.job_position || 'Pozisyon belirtilmemiş'}
              </p>
              
              {/* Email */}
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {member.email}
              </p>
            </div>
          </div>

          {/* Permission Badges Section - Fixed height */}
          <div className="mb-4 h-[70px] flex-shrink-0">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Yetkiler</span>
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-600"></div>
            </div>
            
            <div className="flex flex-wrap gap-1.5 min-h-[40px] ">
              {/* System Role Badge */}
              {member.role === 'super' && (
                <div className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md border border-purple-300">
                  <Crown className="w-3 h-3" />
                  <span>Süper Yönetici</span>
                </div>
              )}
              
              {member.role === 'admin' && (
                <div className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-md border border-orange-300">
                  <ShieldCheck className="w-3 h-3" />
                  <span>Yönetici</span>
                </div>
              )}
              
              {member.role === 'user' && (
                <div className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                  <User className="w-3 h-3" />
                  <span>Kullanıcı</span>
                </div>
              )}

              {/* İş Takip Permission */}
              <div className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border shadow-sm ${
                member.is_takip_permission 
                  ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700' 
                  : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border-red-200 dark:border-red-700'
              }`}>
                {member.is_takip_permission ? (
                  <Activity className="w-3 h-3" />
                ) : (
                  <ShieldX className="w-3 h-3" />
                )}
                <span>{member.is_takip_permission ? 'İş Takip' : 'İş Takip ✗'}</span>
              </div>
              
              {/* Stok Takip Permission */}
              <div className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border shadow-sm ${
                member.stok_takip_permission 
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700' 
                  : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border-red-200 dark:border-red-700'
              }`}>
                {member.stok_takip_permission ? (
                  <Boxes className="w-3 h-3" />
                ) : (
                  <ShieldX className="w-3 h-3" />
                )}
                <span>{member.stok_takip_permission ? 'Stok Takip' : 'Stok Takip ✗'}</span>
              </div>

              {/* Additional Permission Badge for Super Users */}
              {member.role === 'super' && (
                <div className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md border border-indigo-300">
                  <Database className="w-3 h-3" />
                  <span>Tam Erişim</span>
                </div>
              )}
              
              {/* Admin Permissions for Admin Users */}
              {member.role === 'admin' && (
                <div className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md border border-amber-300">
                  <Settings className="w-3 h-3" />
                  <span>Yönetim</span>
                </div>
              )}
            </div>
          </div>

          {/* Stats Section - Fixed height */}
          <div className="grid grid-cols-2 gap-3 mb-4 h-[80px] flex-shrink-0 mt-3">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center border border-blue-100 dark:border-blue-800">
              <div className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                {member.activeTasks}
              </div>
              <div className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                Aktif Görev
              </div>
            </div>
            
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center border border-green-100 dark:border-green-800">
              <div className="text-xl font-bold text-green-600 dark:text-green-400 mb-1">
                {member.completedTasks}
              </div>
              <div className="text-xs text-green-600 dark:text-green-400 font-medium">
                Tamamlanan
              </div>
            </div>
          </div>

          {/* Skills Section - Fixed height */}
          <div className="mb-4 h-[90px] flex-shrink-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Yetenekler</span>
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-600"></div>
            </div>
            
            <div className="flex flex-wrap gap-1.5 h-[60px] overflow-hidden">
              {member.skills && member.skills.length > 0 ? (
                <>
                  {member.skills.slice(0, 4).map((skill, index) => (
                    <span
                      key={index}
                      className="px-2.5 py-1 text-xs font-medium rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600"
                    >
                      {skill}
                    </span>
                  ))}
                  {member.skills.length > 4 && (
                    <span className="px-2.5 py-1 text-xs font-medium rounded-md bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-500">
                      +{member.skills.length - 4}
                    </span>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-xs italic text-gray-400 dark:text-gray-500 animate-pulse">
                    Henüz herhangi bir yetenek eklenmemiş
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Action Button - Fixed at bottom */}
          <div className="pt-6 pb-2 border-t border-gray-100 dark:border-gray-700 flex-shrink-0 mt-auto">
            <div className="w-full py-3 px-4 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-white hover:bg-blue-600 dark:hover:bg-blue-500 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 border border-blue-200 dark:border-blue-700 hover:border-blue-600">
              <span>Detayları Görüntüle</span>
              <ChevronDown className="w-4 h-4 group-hover:rotate-180 transition-transform duration-300" />
            </div>
          </div>
        </div>
      </button>
    );

    return (
      <>
        <ClosedCard />
        
        {/* Modal */}
        <AnimatePresence>
          {isSelected && (
      <motion.div
              
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
              onMouseDown={(e) => {
                // Only close if clicking directly on the backdrop, not its children
                if (e.target === e.currentTarget) {
                  setSelectedMember(null);
                }
              }}
            >
              <motion.div
                
                onMouseDown={(e) => e.stopPropagation()}
                className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-800 rounded-xl shadow-xl"
              >
                <div className="p-6 space-y-6">
                  {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="relative">
                <img
                  src={member.avatar}
                  alt={member.name}
                          className="w-20 h-20 rounded-full ring-4 ring-offset-2 ring-primary-500 cursor-pointer hover:opacity-90 transition-opacity"
                  onMouseDown={(e) => {
                            e.stopPropagation();
                            setShowProfilePhoto(true);
                          }}
                        />
                        <div className={`absolute bottom-0 right-0 w-5 h-5 rounded-full border-2 border-white dark:border-gray-800 ${
                  member.status === 'online' ? 'bg-green-500' : 'bg-gray-400'
                }`} />
                        {isNew(member.startDate) && (
                          <div className="absolute -top-2 -right-2 px-2 py-1 bg-red-500 text-white text-xs font-medium rounded-full shadow-lg">
                            YENİ
                          </div>
                        )}
              </div>
              <div>
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {member.name}
                        </h2>
                        <p className="text-lg text-gray-600 dark:text-gray-300">
                  {member.job_position || 'Pozisyon belirtilmemiş'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onMouseDown={(e) => {
                e.stopPropagation();
                setSelectedMember(null);
              }}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
                      <X className="w-6 h-6 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

                  {/* İletişim Bilgileri */}
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => handleCopyClick(member.email, 'email')}
                      className="group relative flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300 min-w-0 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                    >
                      <Mail className="w-4 h-4 flex-shrink-0 text-primary-500" />
                      <span className="truncate">{member.email}</span>
                      {copiedField === 'email' ? (
                        <CheckCheck className="w-4 h-4 text-green-500 absolute right-2" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity absolute right-2" />
                      )}
                      <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {member.email}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCopyClick(member.phone, 'phone')}
                      className="group relative flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300 min-w-0 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                    >
                      <Phone className="w-4 h-4 flex-shrink-0 text-primary-500" />
                      <span className="truncate">{formatPhoneNumber(member.phone)}</span>
                      {copiedField === 'phone' ? (
                        <CheckCheck className="w-4 h-4 text-green-500 absolute right-2" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity absolute right-2" />
                      )}
                      <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {formatPhoneNumber(member.phone)}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCopyClick(member.location, 'location')}
                      className="group relative flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300 min-w-0 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                    >
                      <MapPin className="w-4 h-4 flex-shrink-0 text-primary-500" />
                      <span className="truncate">{member.location}</span>
                      {copiedField === 'location' ? (
                        <CheckCheck className="w-4 h-4 text-green-500 absolute right-2" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity absolute right-2" />
                      )}
                      <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {member.location}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCopyClick(new Date(member.startDate).toLocaleDateString('tr-TR'), 'date')}
                      className="group relative flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300 min-w-0 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                    >
                      <Calendar className="w-4 h-4 flex-shrink-0 text-primary-500" />
                      <span className="truncate">Katılım: {new Date(member.startDate).toLocaleDateString('tr-TR')}</span>
                      {copiedField === 'date' ? (
                        <CheckCheck className="w-4 h-4 text-green-500 absolute right-2" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity absolute right-2" />
                      )}
                      <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        Katılım: {new Date(member.startDate).toLocaleDateString('tr-TR')}
                      </span>
                    </button>
                  </div>

                  {/* Takip İstatistikleri */}
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-200">
                    <div className="grid grid-cols-2 gap-4">
                      {/* Takipçiler */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            <Users className="w-4 h-4 text-primary-500" />
                            Takipçiler
                          </span>
                          <span className="text-lg font-semibold text-primary-600 dark:text-primary-400">
                            {member.followers?.length || 0}
                          </span>
                        </div>
                        <div className="space-y-3">
                          <div className="flex flex-wrap gap-2">
                            {member.followers?.slice(0, 6).map((follower) => (
                              <div
                                key={follower.id}
                                className="group relative"
                              >
                                <img
                                  src={follower.avatar}
                                  alt={follower.name}
                                  className="w-8 h-8 rounded-full border-2 border-white dark:border-gray-800"
                                />
                                <div className="absolute -top-12 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                  {follower.name}
                                </div>
                              </div>
                            ))}
                            {member.followers?.length > 6 && (
                              <div className="w-8 h-8 rounded-full border-2 border-white dark:border-gray-800 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                                <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                                  +{member.followers.length - 6}
                                </span>
                              </div>
                            )}
                          </div>
                          {member.followers?.length === 0 && (
                            <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                              Henüz takipçi yok
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Takip Edilenler */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            <UserPlus className="w-4 h-4 text-primary-500" />
                            Takip Edilenler
                          </span>
                          <span className="text-lg font-semibold text-primary-600 dark:text-primary-400">
                            {member.following?.length || 0}
                          </span>
                        </div>
                        <div className="space-y-3">
                          <div className="flex flex-wrap gap-2">
                            {member.following?.slice(0, 6).map((followed) => (
                              <div
                                key={followed.id}
                                className="group relative"
                              >
                                <img
                                  src={followed.avatar}
                                  alt={followed.name}
                                  className="w-8 h-8 rounded-full border-2 border-white dark:border-gray-800"
                                />
                                <div className="absolute -top-12 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                  {followed.name}
                                </div>
                              </div>
                            ))}
                            {member.following?.length > 6 && (
                              <div className="w-8 h-8 rounded-full border-2 border-white dark:border-gray-800 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                                <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                                  +{member.following.length - 6}
                                </span>
                              </div>
                            )}
                          </div>
                          {member.following?.length === 0 && (
                            <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                              Henüz kimseyi takip etmiyor
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Takip Et Butonu */}
                    {currentUserId !== member.id && (
                      <button
                        type="button"
                        onClick={() => handleFollow(member.id)}
                        className={`mt-4 w-full py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                          isFollowing
                            ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-red-100 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 group'
                            : 'bg-primary-600 text-white hover:bg-primary-700'
                        }`}
                      >
                        {isFollowing ? (
                          <>
                            <Check className="w-4 h-4 group-hover:hidden" />
                            <X className="w-4 h-4 hidden group-hover:block" />
                            <span className="group-hover:hidden">Takip Ediliyor</span>
                            <span className="hidden group-hover:block">Takibi bırak</span>
                          </>
                        ) : (
                          <>
                            <UserPlus className="w-4 h-4" />
                            Takip Et
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Biyografi */}
                <div className="pt-4 border-t border-gray-200 dark:border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                        <User className="w-4 h-4 text-primary-500" />
                        Biyografi
                      </h4>
                      {/* Düzenleme butonunu her zaman göster */}
                        <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Direkt olarak userRole ile kontrol et
                          if (isCurrentUser || userRole === 'super') {
                            startEditingBio(); // veya startEditingSkills()
                          } else {
                            toast.error('Bu işlemi sadece kendi profiliniz veya süper yönetici yetkisine sahipseniz yapabilirsiniz!');
                          }
                        }}
                          className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500 hover:text-primary-500"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                    </div>
                    {editingBio && selectedMember?.id === member.id ? (
                      <div className="space-y-3">
                        <textarea
                          value={localBio}
                          onChange={(e) => handleLocalBioChange(e.target.value)}
                          className="w-full px-4 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y min-h-[100px] max-h-[300px] transition-all duration-200"
                          placeholder="Kendinizi tanıtın..."
                        />
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingBio(false);
                              // Remove stored edits when canceling
                              setEditedBios(prev => {
                                const newEdits = {...prev};
                                delete newEdits[member.id];
                                return newEdits;
                              });
                              setLocalBio(member.bio);
                            }}
                            className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          >
                            İptal
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSaveBio(member.id, localBio)}
                            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
                          >
                            <Check className="w-4 h-4" />
                            Kaydet
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="relative bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                          <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-gray-50 dark:from-gray-700/50 to-transparent pointer-events-none z-10" />
                          <p 
                            className={`text-sm text-gray-600 dark:text-gray-300 p-4 overflow-y-auto transition-all duration-300 whitespace-pre-wrap ${
                              isExpanded ? 'max-h-[300px]' : 'max-h-[100px]'
                            }`}
                          >
                            {member.bio}
                          </p>
                          <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-gray-50 dark:from-gray-700/50 to-transparent pointer-events-none" />
                        </div>
                        {member.bio.length > 150 && (
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={() => setIsExpanded(!isExpanded)}
                              className="px-3 py-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors flex items-center gap-1"
                            >
                              {isExpanded ? 'Küçült' : 'Devamını Gör'}
                              <ChevronDown 
                                className={`w-4 h-4 transition-transform duration-200 ${
                                  isExpanded ? 'rotate-180' : ''
                                }`}
                              />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                </div>

                  {/* Yetenekler */}
                <div className="pt-4 border-t border-gray-200 dark:border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                        <Code className="w-4 h-4 text-primary-500" />
                    Yetenekler
                  </h4>
                      {/* Düzenleme butonunu her zaman göster */}
                        <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Direkt olarak userRole ile kontrol et
                          if (isCurrentUser || userRole === 'super') {
                            startEditingSkills(); // veya startEditingBio()
                          } else {
                            toast.error('Bu işlemi sadece kendi profiliniz veya süper yönetici yetkisine sahipseniz yapabilirsiniz!');
                          }
                        }}
                          className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500 hover:text-primary-500"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                    </div>
                    {editingSkills && selectedMember?.id === member.id ? (
                      <div className="space-y-3">
                        <div className="flex flex-wrap gap-2 bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg min-h-[4rem]">
                          {localSkills.split(',').filter(Boolean).map((skill, index) => (
                            <span
                              key={index}
                              className="px-3 py-1.5 text-xs font-medium rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 flex items-center gap-2 group"
                            >
                              {skill}
                              <button
                                type="button"
                                onClick={() => handleLocalRemoveSkill(skill)}
                                className="opacity-0 group-hover:opacity-100 hover:text-red-500 transition-opacity"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={localSkillInput}
                            onChange={(e) => setLocalSkillInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleLocalAddSkill()}
                            placeholder="Yeni yetenek ekle..."
                            className="flex-1 px-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                          />
                          <button
                            type="button"
                            onClick={handleLocalAddSkill}
                            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
                          >
                            <Plus className="w-4 h-4" />
                            Ekle
                          </button>
                        </div>
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingSkills(false);
                              // Remove stored edits when canceling
                              setEditedSkills(prev => {
                                const newEdits = {...prev};
                                delete newEdits[member.id];
                                return newEdits;
                              });
                              setLocalSkills(member.skills.join(','));
                            }}
                            className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          >
                            İptal
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSaveSkills(member.id, localSkills.split(',').filter(Boolean))}
                            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
                          >
                            <Check className="w-4 h-4" />
                            Kaydet
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2 bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg min-h-[4rem]">
                    {member.skills.map((skill, index) => (
                      <span
                        key={index}
                            className="px-3 py-1.5 text-xs font-medium rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                    )}
                </div>

                  {/* Görev İstatistikleri */}
                <div className="pt-4 border-t border-gray-200 dark:border-gray-200 grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-gray-600 dark:text-gray-300 flex items-center gap-2">
                          <Clock className="w-4 h-4 text-primary-500" />
                          Aktif Görevler
                        </span>
                      <span className="font-medium text-gray-900 dark:text-white">{member.activeTasks}</span>
                    </div>
                      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                          className="h-full bg-primary-600 rounded-full transition-all duration-500"
                        style={{ width: `${(member.activeTasks / 8) * 100}%` }}
                      />
                    </div>
                  </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-gray-600 dark:text-gray-300 flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          Tamamlanan
                        </span>
                      <span className="font-medium text-gray-900 dark:text-white">{member.completedTasks}</span>
                    </div>
                      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                          className="h-full bg-green-500 rounded-full transition-all duration-500"
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                </div>

                  {/* Eylem Butonları */}
                <div className="pt-4 border-t border-gray-200 dark:border-gray-200">
                  <div className="flex items-center gap-2">
                      {/* Super user profile details button */}
                      {userRole === 'super' && (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setProfileMember(member);
                            setShowProfileDetails(true);
                          }}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 transition-colors"
                        >
                          <UserCog className="w-4 h-4" />
                          Profil Detayları
                        </button>
                      )}
                      
                      {/* Send Account Info Button - Only for admins and super users */}
                      {(userRole === 'super' || userRole === 'admin') && currentUserId !== member.id && (
                        <button 
                          onClick={() => handleSendAccountInfo(member.id, member.email)}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors"
                        >
                          <Mail className="w-4 h-4" />
                          Kişiye Bilgilerini İlet
                        </button>
                      )}
                      
                      {currentUserId !== member.id ? (
                        <button 
                          onClick={() => handleSendMessage(member.id, member.name)}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors ${userRole === 'super' ? '' : 'flex-1'}`}
                        >
                          <MessageSquare className="w-4 h-4" />
                          Mesaj Gönder
                        </button>
                      ) : (
                        <button 
                          disabled
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-300 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-sm font-medium rounded-lg cursor-not-allowed ${userRole === 'super' ? '' : 'flex-1'}`}
                        >
                          <MessageSquare className="w-4 h-4" />
                          Kendinize Mesaj Gönderemezsiniz
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* System Permission Management - ONLY for admins */}
                {isAdmin && (
                  <div className="pt-6 border-t border-gray-200 dark:border-gray-700 p-8">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-500 shadow-sm">
                          <Shield className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                            Sistem Yetkileri
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            Kullanıcının erişebileceği sistemleri yönetin
                          </p>
                        </div>
                      </div>
                      <div className="px-3 py-1.5 text-xs font-medium bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full flex items-center gap-2 shadow-sm">
                        <Lock className="w-3.5 h-3.5" />
                        <span>Yönetici Erişimi</span>
                      </div>
                    </div>
                    
                    <div className="rounded-xl overflow-hidden border border-gray-100 dark:border-gray-700 shadow-lg bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900/80 backdrop-blur-sm transform transition-all duration-300 hover:shadow-xl">
                      {/* Header */}
                      <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800/90 dark:to-gray-800/70 border-b border-gray-100 dark:border-gray-700/70">
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Sistem</div>
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Erişim Durumu</div>
                        </div>
                      </div>
                      
                      <PermissionSettings 
                        member={member} 
                        currentUserId={currentUserId}
                        setTeamMembers={setTeamMembers}
                        setSelectedMember={setSelectedMember}
                        selectedMember={selectedMember}
                        userRole={userRole}
                      />
                    </div>
                  </div>
                )}
              </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

        {/* Profil Fotoğrafı Pop-up */}
        <AnimatePresence>
          {showProfilePhoto && (
            <motion.div
           
              className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80"
              onClick={() => setShowProfilePhoto(false)}
            >
              <motion.div
               
                className="relative max-w-3xl w-full aspect-square"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={member.avatar}
                  alt={member.name}
                  className="w-full h-full object-cover rounded-xl"
                />
                <button
                  type="button"
                  onClick={() => setShowProfilePhoto(false)}
                  className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 rounded-lg text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="backdrop-blur-lg bg-white/20 dark:bg-gray-900/20 border border-white/30 dark:border-gray-700/30 rounded-xl p-6 shadow-xl mr-96">
                    <div className="space-y-1">
                      <h3 className="text-2xl font-semibold text-white flex items-center gap-2">
                        {member.name}
                        {member.status === 'online' && (
                          <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
                        )}
                      </h3>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white">
                          {member.role}
                        </span>
                        {isNew(member.startDate) && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-500/80 text-white">
                            YENİ
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
        </div>
      </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  };

  const AddMemberModal = () => {
    const [newMemberData, setNewMemberData] = useState({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      position: '',
      department: '',
      country: 'Türkiye',
      city: '',
      gender: '',
      bio: '',
      skills: ''
    });
  
    const genderOptions = [
      { value: 'male', label: 'Erkek' },
      { value: 'female', label: 'Kadın' },
      { value: 'other', label: 'Diğer' }
    ];
  
    const positionOptions = [
      // Yönetim ve İdari Pozisyonlar
      { value: 'CEO (Genel Müdür)', label: 'CEO (Genel Müdür)' },
      { value: 'COO (Operasyon Direktörü)', label: 'COO (Operasyon Direktörü)' },
      { value: 'CFO (Finans Direktörü)', label: 'CFO (Finans Direktörü)' },
      { value: 'Genel Müdür', label: 'Genel Müdür' },
      { value: 'İK Müdürü', label: 'İK Müdürü' },
      { value: 'Proje Yöneticisi', label: 'Proje Yöneticisi' },
      { value: 'Operasyon Yöneticisi', label: 'Operasyon Yöneticisi' },
      { value: 'Pazarlama Müdürü', label: 'Pazarlama Müdürü' },
      // Mühendislik ve Teknik Pozisyonlar
      { value: 'Yazılım Mühendisi', label: 'Yazılım Mühendisi' },
      { value: 'Web Geliştirici', label: 'Web Geliştirici' },
      { value: 'Sistem Yöneticisi', label: 'Sistem Yöneticisi' },
      { value: 'Grafik Tasarımcı', label: 'Grafik Tasarımcı' },
      { value: 'UI/UX Tasarımcı', label: 'UI/UX Tasarımcı' },
      { value: 'Pazarlama Uzmanı', label: 'Pazarlama Uzmanı' },
      { value: 'Satış Temsilcisi', label: 'Satış Temsilcisi' },
      { value: 'İnsan Kaynakları Uzmanı', label: 'İnsan Kaynakları Uzmanı' },
      { value: 'Muhasebe Uzmanı', label: 'Muhasebe Uzmanı' },
      { value: 'Stajyer', label: 'Stajyer' },
      { value: 'Diğer', label: 'Diğer' }
    ];
  
    const countryOptions = [
      'Türkiye', 'Amerika Birleşik Devletleri', 'Almanya', 'Fransa', 'İtalya', 
      'İspanya', 'Birleşik Krallık', 'Kanada', 'Avustralya', 'Japonya', 'Diğer'
    ];
  
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
        onClick={() => setIsAddModalOpen(false)}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-800 rounded-xl shadow-xl"
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Yeni Ekip Üyesi Ekle
              </h2>
              <button
                type="button"
                onClick={() => setIsAddModalOpen(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
          </div>
  
          <form className="p-6 space-y-6">
            {/* Profil Fotoğrafı */}
            <div className="flex flex-col items-center">
              <div className="relative">
                <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                  <Upload className="w-8 h-8 text-gray-400" />
                </div>
                <button
                  type="button"
                  className="absolute bottom-0 right-0 p-2 bg-primary-600 rounded-full text-white hover:bg-primary-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Profil fotoğrafı ekleyin
              </p>
            </div>
  
            {/* Kişisel Bilgiler */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ad
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={newMemberData.firstName}
                    onChange={(e) => setNewMemberData({...newMemberData, firstName: e.target.value})}
                    className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="Ad giriniz"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Soyad
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={newMemberData.lastName}
                    onChange={(e) => setNewMemberData({...newMemberData, lastName: e.target.value})}
                    className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="Soyad giriniz"
                  />
                </div>
              </div>
  
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  E-posta
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={newMemberData.email}
                    onChange={(e) => setNewMemberData({...newMemberData, email: e.target.value})}
                    className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="E-posta adresi giriniz"
                  />
                </div>
              </div>
  
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Telefon
                </label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={newMemberData.phone}
                    onChange={(e) => {
                      // Format phone number as "+90 (XXX) XXX XXXX"
                      let value = e.target.value.replace(/\D/g, '');
                      if (value.startsWith('90')) {
                        value = value.substring(2);
                      }
                      if (value.length > 0) {
                        if (value.length <= 3) {
                          value = `+90 (${value}`;
                        } else if (value.length <= 6) {
                          value = `+90 (${value.substring(0, 3)}) ${value.substring(3)}`;
                        } else {
                          value = `+90 (${value.substring(0, 3)}) ${value.substring(3, 6)} ${value.substring(6, 10)}`;
                        }
                      }
                      setNewMemberData({...newMemberData, phone: value});
                    }}
                    className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="+90 (5XX) XXX XXXX"
                    maxLength={18}
                  />
                </div>
              </div>
  
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cinsiyet
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
                  <select
                    value={newMemberData.gender}
                    onChange={(e) => setNewMemberData({...newMemberData, gender: e.target.value})}
                    className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors appearance-none"
                  >
                    <option value="">Cinsiyet seçiniz</option>
                    {genderOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
              </div>
  
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Pozisyon
                </label>
                <div className="relative">
                  <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
                  <select
                    value={newMemberData.position}
                    onChange={(e) => setNewMemberData({...newMemberData, position: e.target.value})}
                    className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors appearance-none"
                  >
                    <option value="">Pozisyon seçiniz</option>
                    {positionOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
              </div>
  
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Şehir
                </label>
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={newMemberData.city}
                    onChange={(e) => setNewMemberData({...newMemberData, city: e.target.value})}
                    className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="Şehir giriniz"
                  />
                </div>
              </div>
  
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ülke
                </label>
                <div className="relative">
                  <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
                  <select
                    value={newMemberData.country}
                    onChange={(e) => setNewMemberData({...newMemberData, country: e.target.value})}
                    className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors appearance-none"
                  >
                    {countryOptions.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>
  
            {/* Yetenekler */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Yetenekler
              </label>
              <div className="relative">
                <Code className="absolute left-4 top-4 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={newMemberData.skills}
                  onChange={(e) => setNewMemberData({...newMemberData, skills: e.target.value})}
                  className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  placeholder="Yetenekleri virgülle ayırarak girin (React, JavaScript, Python)"
                />
              </div>
            </div>
  
            {/* Biyografi */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Biyografi
              </label>
              <div className="relative">
                <User className="absolute left-4 top-4 w-5 h-5 text-gray-400" />
                <textarea
                  rows={4}
                  value={newMemberData.bio}
                  onChange={(e) => setNewMemberData({...newMemberData, bio: e.target.value})}
                  className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors resize-none"
                  placeholder="Kısa bir biyografi yazın..."
                />
              </div>
            </div>
          </form>
  
          <div className="p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsAddModalOpen(false)}
                className="px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                İptal
              </button>
              <button
                type="button"
                className="px-6 py-3 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
              >
                <UserPlus className="w-4 h-4" />
                Üye Ekle
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  // Mesaj gönder butonuna tıklandığında kullanıcı ile sohbet başlatma
  const handleSendMessage = (memberId, memberName) => {
    // Kullanıcı kendisine mesaj gönderemez
    if (memberId === currentUserId) {
      toast.error('Kendinize mesaj gönderemezsiniz');
      return;
    }
    
    // Chat sayfasına yönlendir ve üye bilgilerini state olarak geçir
    navigate('/chat', { 
      state: { 
        startChat: true, 
        userId: memberId,
        userName: memberName
      } 
    });
  };

  // Kullanıcıya hesap bilgilerini e-posta ile gönderme fonksiyonu
  const handleSendAccountInfo = async (userId, userEmail) => {
    try {
      // Önce bir onay mesajı göster
      const confirmed = window.confirm(`${userEmail} adresine hesap bilgilerini göndermek istediğinize emin misiniz?`);
      if (!confirmed) return;
      
      const toastId = toast.loading('Hesap bilgileri gönderiliyor...', { id: 'sendAccountInfo' });
      
      // Backend URL'ini import.meta.env'den al (VITE_LOCALHOST_URL)
      const backendUrl = import.meta.env.VITE_LOCALHOST_URL || 'http://localhost:8080';
      console.log('Backend URL:', backendUrl);
      
      // PLAN B: Mevcut kullanıcı bilgilerini frontend tarafında oluştur
      try {
        // Önce user_profiles tablosundan kullanıcı bilgilerini al
        const { data: userProfile, error: profileError } = await supabase
          .from('user_profiles')
          .select('first_name, last_name, email, phone, job_position')
          .eq('id', userId)
          .single();
        
        if (profileError) throw profileError;
        
        // Basit e-posta içeriği oluştur
        const emailHtml = `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e0e0e0; border-radius: 5px;">
            <h2 style="color: #3b82f6; text-align: center; margin-bottom: 20px;">Hesap Bilgileriniz</h2>
            
            <p>Merhaba <strong>${userProfile.first_name} ${userProfile.last_name}</strong>,</p>
            
            <p>İsteğiniz üzerine hesap bilgileriniz aşağıda yer almaktadır:</p>
            
            <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
              <tr style="background-color: #f8fafc;">
                <td style="padding: 10px; border: 1px solid #e0e0e0; font-weight: bold;">Ad:</td>
                <td style="padding: 10px; border: 1px solid #e0e0e0;">${userProfile.first_name || 'Belirtilmemiş'}</td>
              </tr>
              <tr>
                <td style="padding: 10px; border: 1px solid #e0e0e0; font-weight: bold;">Soyad:</td>
                <td style="padding: 10px; border: 1px solid #e0e0e0;">${userProfile.last_name || 'Belirtilmemiş'}</td>
              </tr>
              <tr style="background-color: #f8fafc;">
                <td style="padding: 10px; border: 1px solid #e0e0e0; font-weight: bold;">E-posta:</td>
                <td style="padding: 10px; border: 1px solid #e0e0e0;">${userProfile.email || 'Belirtilmemiş'}</td>
              </tr>
              <tr>
                <td style="padding: 10px; border: 1px solid #e0e0e0; font-weight: bold;">Telefon:</td>
                <td style="padding: 10px; border: 1px solid #e0e0e0;">${userProfile.phone || 'Belirtilmemiş'}</td>
              </tr>
              <tr style="background-color: #f8fafc;">
                <td style="padding: 10px; border: 1px solid #e0e0e0; font-weight: bold;">Pozisyon:</td>
                <td style="padding: 10px; border: 1px solid #e0e0e0;">${userProfile.job_position || 'Belirtilmemiş'}</td>
              </tr>
            </table>
            
            <p style="margin-top: 20px;">Güvenliğiniz için hesap bilgilerinizi kimseyle paylaşmayınız.</p>
            
            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e0e0e0; text-align: center; color: #6b7280; font-size: 14px;">
              <p>Bu e-posta İş Takip Sistemi tarafından otomatik olarak gönderilmiştir.</p>
            </div>
          </div>
        `;
        
        // Alternatif: Genel e-posta gönderme API'sini kullan
        const response = await fetch(`${backendUrl}/api/send-email`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ 
            to: userEmail,
            subject: 'Hesap Bilgileriniz - İş Takip Sistemi',
            html: emailHtml
          }),
        });
        
        const responseText = await response.text();
        console.log('Response text:', responseText);
        
        let responseData;
        try {
          responseData = JSON.parse(responseText);
        } catch (err) {
          console.error('JSON parse error:', err);
        }
        
        if (response.ok && responseData?.success) {
          toast.dismiss(toastId);
          toast.success(`Hesap bilgileri ${userEmail} adresine başarıyla gönderildi`);
        } else {
          throw new Error(responseData?.message || 'E-posta gönderirken bir hata oluştu');
        }
        
      } catch (error) {
        console.error('E-posta gönderme hatası:', error);
        toast.dismiss(toastId);
        toast.error('Hesap bilgileri gönderilirken bir hata oluştu: ' + error.message);
      }
      
    } catch (error) {
      console.error('Hesap bilgileri gönderilirken hata:', error);
      toast.dismiss('sendAccountInfo');
      toast.error(error.message || 'Hesap bilgileri gönderilirken bir hata oluştu');
    }
  };

  // Function to update user permissions
  const updateUserPermissions = async (userId, permissionType, value) => {
    try {
      // Kendi yetkini kapatmayı engelle
      if (userId === currentUserId && value === false) {
        toast.error('Kendi yetkinizi kapatamazsınız!');
        return;
      }

      // Get current user's role
      const { data: currentUserProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', currentUserId)
        .single();

      if (profileError) {
        toast.error('Kullanıcı bilgileri alınamadı');
        return;
      }

      // Only 'super' users can change permissions, 'admin' users cannot
      if (currentUserProfile.role !== 'super') {
        toast.error('Sadece süper yöneticiler erişim yetkilerini değiştirebilir');
        return;
      }

      // Show loading state
      const loadingToast = toast.loading(`${permissionType === 'is_takip_permission' ? 'İş Takip' : 'Stok Takip'} yetkisi güncelleniyor...`);

      // Update the permission in the database
      const { error } = await supabase
        .from('user_profiles')
        .update({ [permissionType]: value })
        .eq('id', userId);

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      if (error) {
        console.error('Permission update error:', error);
        toast.error(`Yetki güncellenirken bir hata oluştu: ${error.message}`);
        return;
      }

      // Update the local state
      setTeamMembers(prev => 
        prev.map(member => 
          member.id === userId 
            ? { ...member, [permissionType]: value } 
            : member
        )
      );

      // If this is the selected member, update that state too
      if (selectedMember?.id === userId) {
        setSelectedMember(prev => ({
          ...prev,
          [permissionType]: value
        }));
      }

      // Show success message
      toast.success(`${permissionType === 'is_takip_permission' ? 'İş Takip' : 'Stok Takip'} yetkisi ${value ? 'verildi' : 'kaldırıldı'}`);
    } catch (error) {
      console.error('Error updating permissions:', error);
      toast.error('Yetki güncellenirken bir hata oluştu');
    }
  };

  // Function to update user role
  const updateUserRole = async (userId, newRole) => {
    try {
      // Kendi rolünü düşürmeyi engelle
      if (userId === currentUserId && (newRole !== 'super' && newRole !== 'admin')) {
        toast.error('Kendi rolünüzü düşüremezsiniz!');
        return;
      }
      
      // Get current user's role
      const { data: currentUserProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', currentUserId)
        .single();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        toast.error('Kullanıcı bilgileri alınamadı');
        return;
      }

      // Get target user's role
      const { data: targetUserProfile, error: targetProfileError } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', userId)
        .single();

      if (targetProfileError) {
        console.error('Error fetching target user profile:', targetProfileError);
        toast.error('Hedef kullanıcı bilgileri alınamadı');
        return;
      }

      // Implement permission rules:
      
      // Only super users can promote to super
      if (newRole === 'super' && currentUserProfile.role !== 'super') {
        toast.error('Sadece süper yöneticiler süper yönetici atayabilir');
        return;
      }
      
      // Admin users can't downgrade other admin users
      /*
      if (currentUserProfile.role === 'admin' && 
          targetUserProfile.role === 'admin' && 
          newRole === 'user') {
        toast.error('Admin kullanıcılar diğer admin kullanıcıların yetkisini düşüremez');
        return;
      }
      */
      
      // Admin users can't change super users' roles
      if (currentUserProfile.role === 'admin' && targetUserProfile.role === 'super') {
        toast.error('Admin kullanıcılar süper yöneticilerin rollerini değiştiremez');
        return;
      }

      // Show loading state
      const loadingToast = toast.loading(`Kullanıcı rolü güncelleniyor...`);

      // Update the role in the database
      const { error } = await supabase
        .from('user_profiles')
        .update({ role: newRole })
        .eq('id', userId);

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      if (error) {
        console.error('Role update error:', error);
        toast.error(`Rol güncellenirken bir hata oluştu: ${error.message}`);
        return;
      }

      // Update the local state
      setTeamMembers(prev => 
        prev.map(member => 
          member.id === userId 
            ? { ...member, role: newRole } 
            : member
        )
      );

      // If this is the selected member, update that state too
      if (selectedMember?.id === userId) {
        setSelectedMember(prev => ({
          ...prev,
          role: newRole
        }));
      }

      // Show success message
      toast.success(`Kullanıcı rolü ${newRole} olarak güncellendi`);
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Rol güncellenirken bir hata oluştu');
    }
  };

  // Departman ve rolleri veritabanından çeken fonksiyon
  const fetchDepartmentsAndRoles = async () => {
    try {
      // Job position (departman) bilgilerini getir
      const { data: jobPositions, error: jobError } = await supabase
        .from('user_profiles')
        .select('job_position')
        .not('job_position', 'is', null);

      if (jobError) throw jobError;

      // Benzersiz departmanları ayıkla
      const uniqueDepartments = [...new Set(jobPositions
        .map(item => item.job_position)
        .filter(Boolean)  // null ve undefined değerleri filtrele
      )];

      // Departman listesini oluştur
      const departmentsData = [
        { id: 'all', label: 'Tüm Departmanlar', icon: Users },
        ...uniqueDepartments.map(dept => ({
          id: dept.toLowerCase().replace(/\s+/g, '_'),
          label: dept,
          icon: getIconForDepartment(dept)
        }))
      ];

      setDepartmentsList(departmentsData);

    } catch (error) {
      console.error('Departman ve roller getirilirken hata:', error);
    }
  };

  // Departman adına göre uygun icon döndüren yardımcı fonksiyon
  const getIconForDepartment = (deptName) => {
    const lowerDept = deptName.toLowerCase();
    if (lowerDept.includes('yazılım') || lowerDept.includes('geliştir') || lowerDept.includes('developer')) 
      return Code;
    if (lowerDept.includes('tasar') || lowerDept.includes('design'))
      return Briefcase;
    if (lowerDept.includes('pazar') || lowerDept.includes('marketing'))
      return Building2;
    if (lowerDept.includes('yönet') || lowerDept.includes('manage') || lowerDept.includes('lead'))
      return Users;
    // Varsayılan icon
    return Users;
  };

  // Calculate account age
  const calculateAccountAge = (createdAt) => {
    if (!createdAt) return 'Bilinmiyor';
    
    const now = new Date();
    const created = new Date(createdAt);
    const diffTime = Math.abs(now - created);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) {
      return `${diffDays} gün`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} ay`;
    } else {
      const years = Math.floor(diffDays / 365);
      return `${years} yıl`;
    }
  };

  // Telefon numarası formatlama fonksiyonu
  const formatPhoneNumber = (phone) => {
    if (!phone) return 'Belirtilmemiş';
    
    // Sadece rakamları al
    const cleaned = phone.replace(/\D/g, '');
    
    // Türkiye numarası kontrolü ve formatlama
    if (cleaned.startsWith('90') && cleaned.length === 12) {
      // 905551234567 -> +90 (555) 123 4567
      const countryCode = cleaned.substring(0, 2);
      const areaCode = cleaned.substring(2, 5);
      const firstPart = cleaned.substring(5, 8);
      const secondPart = cleaned.substring(8, 12);
      return `+${countryCode} (${areaCode}) ${firstPart} ${secondPart}`;
    } else if (cleaned.startsWith('5') && cleaned.length === 10) {
      // 5551234567 -> +90 (555) 123 4567
      const areaCode = cleaned.substring(0, 3);
      const firstPart = cleaned.substring(3, 6);
      const secondPart = cleaned.substring(6, 10);
      return `+90 (${areaCode}) ${firstPart} ${secondPart}`;
    } else if (cleaned.length === 11 && cleaned.startsWith('0')) {
      // 05551234567 -> +90 (555) 123 4567
      const areaCode = cleaned.substring(1, 4);
      const firstPart = cleaned.substring(4, 7);
      const secondPart = cleaned.substring(7, 11);
      return `+90 (${areaCode}) ${firstPart} ${secondPart}`;
    }
    
    // Diğer ülke kodları veya bilinmeyen formatlar için basit formatlama
    if (cleaned.length >= 10) {
      return `+${cleaned}`;
    }
    
    return phone; // Orijinal halini döndür
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        {/* Başlık ve Araçlar */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Ekip
              </h2>
              <div className="flex items-center gap-3 ml-4">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {teamMembers.filter(m => m.status === 'online').length} Çevrimiçi
                  </span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                  <Users className="w-4 h-4 text-primary-500" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {teamMembers.length} Üye
                  </span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                  <Star className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {teamMembers.filter(m => isNew(m.startDate)).length} Yeni
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="text"
                  placeholder="İsim, rol, e-posta veya yetenek ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full sm:w-80 pl-10 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 rounded-lg transition-colors ${
                  showFilters
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <Filter className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Filtreler */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
              
                className="mt-4 overflow-hidden"
              >
                <div className="p-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-6">
                  {/* Departman Filtreleri */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-primary-500" />
                      Departman
                    </h4>
                    <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                        {[...departmentsList, { id: 'other', label: 'Diğer', icon: Plus }].map((department) => (
                        <button
                          type="button"
                          key={department.id}
                            onClick={() => {
                              setDepartmentFilter(department.id);
                              if (department.id === 'other') {
                                setShowCustomDepartment(true);
                              } else {
                                setShowCustomDepartment(false);
                                setCustomDepartment('');
                              }
                            }}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                            departmentFilter === department.id
                              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                            <department.icon className="w-4 h-4" />
                          {department.label}
                        </button>
                      ))}
                    </div>
                      {showCustomDepartment && (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={customDepartment}
                            onChange={(e) => setCustomDepartment(e.target.value)}
                            placeholder="Departman ara..."
                            className="flex-1 px-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                  </div>
                      )}
                    </div>
                  </div>

                  {/* Rol Filtreleri */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4 text-primary-500" />
                      Rol
                    </h4>
                    <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                        {rolesList.map((role) => (
                        <button
                          type="button"
                          key={role.id}
                            onClick={() => {
                              setRoleFilter(role.id);
                              if (role.id === 'other') {
                                setShowCustomRole(true);
                              } else {
                                setShowCustomRole(false);
                                setCustomRole('');
                              }
                            }}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                            roleFilter === role.id
                              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                            <role.icon className="w-4 h-4" />
                          {role.label}
                        </button>
                      ))}
                    </div>
                      {showCustomRole && (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={customRole}
                            onChange={(e) => setCustomRole(e.target.value)}
                            placeholder="Rol ara..."
                            className="flex-1 px-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Durum Filtreleri */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary-500" />
                      Durum
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {STATUS_OPTIONS.map((status) => (
                        <button
                          type="button"
                          key={status.id}
                          onClick={() => setStatusFilter(status.id)}
                          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                            statusFilter === status.id
                              ? `bg-${status.color}-100 dark:bg-${status.color}-900/30 text-${status.color}-700 dark:text-${status.color}-400`
                              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          <div className={`w-2 h-2 rounded-full bg-${status.color}-500`} />
                          {status.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Sıralama Seçenekleri */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                      <Filter className="w-4 h-4 text-primary-500" />
                      Sıralama
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {SORT_OPTIONS.map((option) => (
                        <button
                          type="button"
                          key={option.id}
                          onClick={() => setSortBy(option.id)}
                          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                            sortBy === option.id
                              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          <option.icon className={`w-4 h-4 ${
                            option.id.includes('desc') ? 'rotate-180' : ''
                          }`} />
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Filtre Sonuçları */}
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {filteredMembers.length} üye bulundu
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setDepartmentFilter('all');
                          setRoleFilter('all');
                          setStatusFilter('all');
                          setSortBy('name_asc');
                          setSearchQuery('');
                        }}
                        className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                      >
                        Filtreleri Temizle
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Ekip Listesi */}
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedMembers.map((member) => (
                  <div key={member.id} className="relative">
                    <MemberCard member={member} userRole={userRole} />
                  </div>
              ))}
            </div>
            
            {/* Pagination and Refresh Zone */}
            {filteredMembers.length > 0 && (
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-600">
                {/* Results Info and Refresh Button */}
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredMembers.length)}
                    </span>
                    {' '}/ {filteredMembers.length} üye gösteriliyor
                  </div>
                  
                  <button
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                    {isRefreshing ? 'Yenileniyor...' : 'Yenile'}
                  </button>
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    {/* First Page */}
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="p-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronsLeft className="w-4 h-4" />
                    </button>
                    
                    {/* Previous Page */}
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="p-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>

                    {/* Page Numbers */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                              currentPage === pageNum
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    {/* Next Page */}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="p-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    
                    {/* Last Page */}
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="p-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronsRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {/* No Results Message */}
            {filteredMembers.length === 0 && (
              <div className="text-center py-12">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                    <Users className="w-8 h-8 text-gray-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      Ekip üyesi bulunamadı
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400">
                      Arama kriterlerinize uygun ekip üyesi bulunmuyor. Farklı filtreler deneyin.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
          )}
        </div>
      </div>

      {/* Add Member Modal */}
      <AnimatePresence>
        {isAddModalOpen && <AddMemberModal />}
      </AnimatePresence>
      
      {/* Profile Details Modal */}
      <ProfileDetailsModal 
        member={profileMember} 
        isOpen={showProfileDetails} 
        onClose={() => setShowProfileDetails(false)}
        onProfileUpdate={fetchTeamMembers}
      />
    </div>
  );
};

export default Team; 

// Add a new component for permission settings with local state and save button
const PermissionSettings = ({ member, currentUserId, setTeamMembers, setSelectedMember, selectedMember, userRole }) => {
  // Component yüklendikten sonra member verisini tekrar getirerek taze başlayalım
  const [localMember, setLocalMember] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  
  // İzinler için yerel durum değişkenleri - stok_delete_permission eklendi
  const [localPermissions, setLocalPermissions] = useState({
    is_takip_permission: false,
    stok_takip_permission: false,
    stok_auto_permission: false,
    stok_add_permission: false,
    stok_edit_permission: false,
    stok_delete_permission: false, // Silme izni ayrı bir izin olarak eklendi
    stok_movement_permission: false,
    role: 'user'
  });
  
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Component render edildiğinde ve member ID değiştiğinde en taze verileri al
  useEffect(() => {
    const fetchMemberData = async () => {
      setIsFetching(true);
      
      try {
        // İlgili kullanıcıyı veritabanından taze olarak al
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*, is_takip_permission, stok_takip_permission, role, stok_delete_permission')
          .eq('id', member.id)
          .single();
          
        if (error) {
          console.error("Kullanıcı bilgileri çekilirken hata:", error);
          // Hata durumunda mevcut verileri kullan
          setLocalMember(member);
          setLocalPermissions({
            is_takip_permission: Boolean(member.is_takip_permission), 
            stok_takip_permission: Boolean(member.stok_takip_permission),
            stok_auto_permission: Boolean(member.stok_auto_permission),
            stok_add_permission: Boolean(member.stok_add_permission),
            stok_edit_permission: Boolean(member.stok_edit_permission),
            stok_delete_permission: Boolean(member.stok_delete_permission), // Yeni eklenen izin
            stok_movement_permission: Boolean(member.stok_movement_permission),
            role: member.role || 'user'
          });
        } else {
          // console.log("Taze kullanıcı verileri alındı:", data);
          setLocalMember(data);
          // Veritabanından gelen verileri kullan - null kontrolü yap
          setLocalPermissions({
            is_takip_permission: data.is_takip_permission === null ? false : Boolean(data.is_takip_permission),
            stok_takip_permission: data.stok_takip_permission === null ? false : Boolean(data.stok_takip_permission),
            stok_auto_permission: data.stok_auto_permission === null ? false : Boolean(data.stok_auto_permission),
            stok_add_permission: data.stok_add_permission === null ? false : Boolean(data.stok_add_permission),
            stok_edit_permission: data.stok_edit_permission === null ? false : Boolean(data.stok_edit_permission),
            stok_delete_permission: data.stok_delete_permission === null ? false : Boolean(data.stok_delete_permission), // Yeni eklenen izin
            stok_movement_permission: data.stok_movement_permission === null ? false : Boolean(data.stok_movement_permission),
            role: data.role || 'user'
          });
        }
      } catch (error) {
        console.error("Veri çekme hatası:", error);
      } finally {
        setIsFetching(false);
        setHasChanges(false);
      }
    };
    
    if (member && member.id) {
      fetchMemberData();
    }
  }, [member.id]);
  
  // Handle local permission changes - otomatik yetkilendirme mantığı güncellendi
  const handlePermissionChange = (field, value) => {
    let updatedPermissions = {
      ...localPermissions,
      [field]: value
    };

    // Otomatik mod aktifleştirildiğinde kullanıcı rolüne göre izinleri ayarla
    if (field === 'stok_auto_permission' && value === true) {
      // Kullanıcı rolüne göre izinleri ayarla
      if (localPermissions.role === 'super') {
        // Super kullanıcılar için tüm izinler açık
        updatedPermissions = {
          ...updatedPermissions,
          stok_add_permission: true,
          stok_edit_permission: true,
          stok_delete_permission: true,
          stok_movement_permission: true
        };
      } else if (localPermissions.role === 'admin') {
        // Admin kullanıcılar için ekleme ve giriş/çıkış izni açık, düzenleme ve silme kapalı
        updatedPermissions = {
          ...updatedPermissions,
          stok_add_permission: true,
          stok_edit_permission: true,
          stok_delete_permission: false,
          stok_movement_permission: true
        };
      } else {
        // User rolü için tüm izinler kapalı
        updatedPermissions = {
          ...updatedPermissions,
          stok_add_permission: false,
          stok_edit_permission: false,
          stok_delete_permission: false,
          stok_movement_permission: false
        };
      }
    } else if (field === 'role' && localPermissions.stok_auto_permission) {
      // Rol değiştirildiğinde ve otomatik yetkilendirme açıksa izinleri güncelle
      if (value === 'super') {
        // Super kullanıcılar için tüm izinler açık
        updatedPermissions = {
          ...updatedPermissions,
          stok_add_permission: true,
          stok_edit_permission: true,
          stok_delete_permission: true,
          stok_movement_permission: true
        };
      } else if (value === 'admin') {
        // Admin kullanıcılar için ekleme ve giriş/çıkış izni açık, düzenleme ve silme kapalı
        updatedPermissions = {
          ...updatedPermissions,
          stok_add_permission: true,
          stok_edit_permission: true,
          stok_delete_permission: false,
          stok_movement_permission: true
        };
      } else {
        // User rolü için tüm izinler kapalı
        updatedPermissions = {
          ...updatedPermissions,
          stok_add_permission: false,
          stok_edit_permission: false,
          stok_delete_permission: false,
          stok_movement_permission: false
        };
      }
    }

    setLocalPermissions(updatedPermissions);
    setHasChanges(true);
  };
  
  // Save all changes - Yeni eklenen stok_delete_permission'ı da ekle
  const saveChanges = async () => {
    setIsSaving(true);
    try {
      // Fetch current user's role
      const { data: currentUserProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', currentUserId)
        .single();
        
      if (profileError) {
        toast.error('Kullanıcı bilgileri alınamadı');
        setIsSaving(false);
        return;
      }
      
      // Get target user's current role
      const { data: targetUserProfile, error: targetProfileError } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', member.id)
        .single();
        
      if (targetProfileError) {
        toast.error('Hedef kullanıcı bilgileri alınamadı');
        setIsSaving(false);
        return;
      }
      
      // İzin kontrolleri...
      const currentMemberPermissions = localMember || member;
      
      // İş takip ve stok takip izinlerini kontrol et
      if (localPermissions.is_takip_permission !== currentMemberPermissions.is_takip_permission || 
          localPermissions.stok_takip_permission !== currentMemberPermissions.stok_takip_permission) {
        if (currentUserProfile.role !== 'super') {
          toast.error('Sadece süper yöneticiler erişim yetkilerini değiştirebilir');
          setLocalPermissions(prev => ({
            ...prev,
            is_takip_permission: Boolean(currentMemberPermissions.is_takip_permission),
            stok_takip_permission: Boolean(currentMemberPermissions.stok_takip_permission)
          }));
          setIsSaving(false);
          return;
        }
      }
      
      // Rol kontrolü...
      
      // Only super users can promote to super
      if (localPermissions.role === 'super' && currentUserProfile.role !== 'super') {
        toast.error('Sadece süper yöneticiler süper yönetici atayabilir');
        setLocalPermissions(prev => ({
          ...prev,
          role: currentMemberPermissions.role || 'user'
        }));
        setIsSaving(false);
        return;
      }
      
      // Admin users can't downgrade other admin users
      if (currentUserProfile.role === 'admin' && 
          targetUserProfile.role === 'admin' && 
          localPermissions.role === 'user') {
        toast.error('Admin kullanıcılar diğer admin kullanıcıların yetkisini düşüremez');
        setLocalPermissions(prev => ({
          ...prev,
          role: currentMemberPermissions.role || 'user'
        }));
        setIsSaving(false);
        return;
      }
      
      // Admin users can't change super users' roles
      if (currentUserProfile.role === 'admin' && targetUserProfile.role === 'super') {
        toast.error('Admin kullanıcılar süper yöneticilerin rollerini değiştiremez');
        setLocalPermissions(prev => ({
          ...prev,
          role: currentMemberPermissions.role || 'user'
        }));
        setIsSaving(false);
        return;
      }
      
      // console.log("Kayıt için gönderilen izinler:", localPermissions);
      
      // If we reach here, we can make the update - stok_delete_permission eklendi
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({
          is_takip_permission: localPermissions.is_takip_permission,
          stok_takip_permission: localPermissions.stok_takip_permission,
          stok_auto_permission: localPermissions.stok_auto_permission,
          stok_add_permission: localPermissions.stok_add_permission,
          stok_edit_permission: localPermissions.stok_edit_permission,
          stok_delete_permission: localPermissions.stok_delete_permission, // Yeni eklenen izin
          stok_movement_permission: localPermissions.stok_movement_permission,
          role: localPermissions.role
        })
        .eq('id', member.id);
        
      if (updateError) {
        console.error("Güncelleme hatası:", updateError);
        toast.error(`Güncelleme hatası: ${updateError.message}`);
        setIsSaving(false);
        return;
      }
      
      // Güncellenmiş kullanıcı bilgisini al
      const { data: updatedMember, error: fetchError } = await supabase
        .from('user_profiles')
        .select('*, is_takip_permission, stok_takip_permission, role, stok_delete_permission')
        .eq('id', member.id)
        .single();
        
      if (fetchError) {
        console.error("Güncellenmiş kullanıcı bilgisi alınamadı:", fetchError);
      } else {
        // Local state'i güncellenmiş verilerle güncelle
        setLocalMember(updatedMember);
        // console.log("Güncellenmiş kullanıcı verileri:", updatedMember);
      }
      
      // Update local state in the parent component - stok_delete_permission eklendi
      const updatedMemberData = updatedMember || {
        ...member,
        is_takip_permission: localPermissions.is_takip_permission,
        stok_takip_permission: localPermissions.stok_takip_permission,
        stok_auto_permission: localPermissions.stok_auto_permission,
        stok_add_permission: localPermissions.stok_add_permission,
        stok_edit_permission: localPermissions.stok_edit_permission,
        stok_delete_permission: localPermissions.stok_delete_permission,
        stok_movement_permission: localPermissions.stok_movement_permission,
        role: localPermissions.role
      };
      
      setTeamMembers(prev => 
        prev.map(m => 
          m.id === member.id 
            ? { 
                ...m, 
                is_takip_permission: updatedMemberData.is_takip_permission,
                stok_takip_permission: updatedMemberData.stok_takip_permission,
                stok_auto_permission: updatedMemberData.stok_auto_permission,
                stok_add_permission: updatedMemberData.stok_add_permission,
                stok_edit_permission: updatedMemberData.stok_edit_permission,
                stok_delete_permission: updatedMemberData.stok_delete_permission,
                stok_movement_permission: updatedMemberData.stok_movement_permission,
                role: updatedMemberData.role 
              } 
            : m
        )
      );
      
      // Update selected member if this is the one
      // selectedMember undefined olma durumunu kontrol edelim
      if (selectedMember && selectedMember.id === member.id) {
        try {
          setSelectedMember(prevMember => {
            if (prevMember) {
              return {
                ...prevMember,
                is_takip_permission: updatedMemberData.is_takip_permission,
                stok_takip_permission: updatedMemberData.stok_takip_permission,
                stok_auto_permission: updatedMemberData.stok_auto_permission,
                stok_add_permission: updatedMemberData.stok_add_permission,
                stok_edit_permission: updatedMemberData.stok_edit_permission,
                stok_delete_permission: updatedMemberData.stok_delete_permission,
                stok_movement_permission: updatedMemberData.stok_movement_permission,
                role: updatedMemberData.role
              };
            }
            return prevMember;
          });
        } catch (err) {
          console.error("selectedMember güncellenirken hata:", err);
          // Hatayı yuttuk, işleme devam edebiliriz
        }
      }
      
      toast.success('Kullanıcı yetkileri başarıyla güncellendi');
      setHasChanges(false);
      
    } catch (error) {
      console.error('Error saving permissions:', error);
      toast.error('Yetkiler güncellenirken bir hata oluştu');
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <>
      {/* İş Takip Permission */}
      <div className="group transition-all duration-300 hover:bg-blue-50/30 dark:hover:bg-blue-900/10  rounded-bl-[24px] rounded-br-[24px]">
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-800/20 text-blue-600 dark:text-blue-400 shadow-sm transform transition-transform duration-300 group-hover:scale-110">
              <Briefcase className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                İş Takip Sistemi
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
                {isFetching ? (
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2 bg-gray-300 dark:bg-gray-600 rounded-full animate-pulse"></span>
                    <span>Yükleniyor...</span>
                  </span>
                ) : localPermissions.is_takip_permission ? (
                  <>
                    <span className="flex items-center gap-1.5">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                      </span>
                      <span>Erişim yetkisi aktif</span>
                    </span>
                  </>
                ) : (
                  <>
                    <span className="flex items-center gap-1.5">
                      <span className="relative flex h-2 w-2">
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-gray-400"></span>
                      </span>
                      <span>Erişim yetkisi pasif</span>
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              className="sr-only peer" 
              checked={Boolean(localPermissions.is_takip_permission)}
              onChange={() => handlePermissionChange('is_takip_permission', !localPermissions.is_takip_permission)}
              disabled={currentUserId === member.id || isFetching}
            />
            <div className={`w-11 h-6 rounded-full ${currentUserId === member.id || isFetching ? 'bg-gray-300 dark:bg-gray-600' : 'bg-gray-200 dark:bg-gray-700'} peer-focus:ring-2 peer-focus:ring-offset-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 peer-checked:bg-gradient-to-r peer-checked:from-blue-400 peer-checked:to-blue-600 dark:peer-checked:from-blue-500 dark:peer-checked:to-blue-700 transition-all duration-300 ease-in-out after:content-[''] after:absolute after:bg-white after:border-0 after:rounded-full after:h-5 after:w-5 after:left-0.5 after:top-0.5 after:shadow-md after:transition-all after:duration-300 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full ${currentUserId === member.id || isFetching ? 'opacity-70 cursor-not-allowed' : ''}`}></div>
          </label>
        </div>
      </div>
      
      {/* Divider - with gradient */}
      <div className="mx-6 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent"></div>
      
      {/* Stok Takip Permission */}
      <div className="group transition-all duration-300 hover:bg-green-50/30 dark:hover:bg-green-900/10 rounded-[24px]">
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-900/20 dark:to-green-900/10 text-green-600 dark:text-green-400 shadow-sm transform transition-transform duration-300 group-hover:scale-110">
              <Package2 className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors duration-300">
                Stok Takip Sistemi
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
                {isFetching ? (
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2 bg-gray-300 dark:bg-gray-600 rounded-full animate-pulse"></span>
                    <span>Yükleniyor...</span>
                  </span>
                ) : localPermissions.stok_takip_permission ? (
                  <>
                    <span className="flex items-center gap-1.5">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                      </span>
                      <span>Erişim yetkisi aktif</span>
                    </span>
                  </>
                ) : (
                  <>
                    <span className="flex items-center gap-1.5">
                      <span className="relative flex h-2 w-2">
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-gray-400"></span>
                      </span>
                      <span>Erişim yetkisi pasif</span>
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              className="sr-only peer" 
              checked={Boolean(localPermissions.stok_takip_permission)}
              onChange={() => handlePermissionChange('stok_takip_permission', !localPermissions.stok_takip_permission)}
              disabled={currentUserId === member.id || isFetching}
            />
            <div className={`w-11 h-6 rounded-full ${currentUserId === member.id || isFetching ? 'bg-gray-300 dark:bg-gray-600' : 'bg-gray-200 dark:bg-gray-700'} peer-focus:ring-2 peer-focus:ring-offset-2 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 peer-checked:bg-gradient-to-r peer-checked:from-green-400 peer-checked:to-green-600 dark:peer-checked:from-green-500 dark:peer-checked:to-green-700 transition-all duration-300 ease-in-out after:content-[''] after:absolute after:bg-white after:border-0 after:rounded-full after:h-5 after:w-5 after:left-0.5 after:top-0.5 after:shadow-md after:transition-all after:duration-300 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full ${currentUserId === member.id || isFetching ? 'opacity-70 cursor-not-allowed' : ''}`}></div>
          </label>
        </div>
      </div>
      
      {/* Divider - with gradient */}
      <div className="mx-6 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent"></div>
      
      {/* User Role Selection */}
      <div className="group transition-all duration-300 hover:bg-purple-50/30 dark:hover:bg-purple-900/10 rounded-tr-[24px] rounded-tl-[24px]">
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-gradient-to-br from-purple-50 to-purple-100/50 dark:from-purple-900/20 dark:to-purple-900/10 text-purple-600 dark:text-purple-400 shadow-sm transform transition-transform duration-300 group-hover:scale-110">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-300">
                Kullanıcı Rolü
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
                {isFetching ? (
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2 bg-gray-300 dark:bg-gray-600 rounded-full animate-pulse"></span>
                    <span>Yükleniyor...</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5">
                    <span className={`relative flex h-2 w-2 ${
                      localPermissions.role === 'super' ? 'bg-purple-500' : 
                      localPermissions.role === 'admin' ? 'bg-blue-500' : 'bg-gray-500'
                    }`}>
                    </span>
                    <span>Mevcut rol: {
                      localPermissions.role === 'super' ? 'Süper Yönetici' :
                      localPermissions.role === 'admin' ? 'Admin Yetkili Kişi' :
                      localPermissions.role === 'user' ? 'Yetkisiz Kişi' : localPermissions.role || 'user'
                    }</span>
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="relative">
            <select
              value={localPermissions.role}
              onChange={(e) => handlePermissionChange('role', e.target.value)}
              className={`appearance-none bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 py-2 px-4 pr-8 rounded-lg leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${(currentUserId === member.id || isFetching) ? 'opacity-70 cursor-not-allowed' : ''}`}
              disabled={currentUserId === member.id || isFetching}
            >
              <option value="user">Kullanıcı</option>
              <option value="admin">Yönetici</option>
              <option value="super">Süper Yönetici</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-gray-300">
              {/* <ChevronDown className="w-4 h-4" /> */}
            </div>
          </div>
        </div>
      </div>
      
      {/* Özel dekoratif ayraç - Kullanıcı Rolü ve Stok Takip Seçenekleri arasında */}
      <div className="relative my-3 mt-5 mb-5">
        <div className="h-1.5 bg-gradient-to-r from-transparent via-indigo-500/40 dark:via-indigo-400/40 to-transparent rounded-full mx-6"></div>
        <div className="h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent absolute inset-x-6 top-1/2 transform -translate-y-1/2"></div>
        <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-gradient-to-br from-indigo-100 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/20 flex items-center justify-center shadow-sm">
          <Shield className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
        </div>
        
      </div>

      {/* Stok Takip Yetkiler Bölümü - Mevcut stok_takip_permission sonrasına ekle */}
      <div className="group transition-all duration-300 hover:bg-yellow-50/30 dark:hover:bg-yellow-900/10 rounded-br-[24px] rounded-bl-[24px]">
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-gradient-to-br from-yellow-50 to-yellow-100/50 dark:from-yellow-900/20 dark:to-yellow-900/10 text-yellow-600 dark:text-yellow-400 shadow-sm transform transition-transform duration-300 group-hover:scale-110">
              <Package2 className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-yellow-600 dark:group-hover:text-yellow-400 transition-colors duration-300">
                Otomatik Yetkilendirme 
                <button 
                  type="button"
                  className="ml-1.5 relative inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-yellow-100 dark:hover:bg-yellow-900/20 hover:text-yellow-600 dark:hover:text-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-300 dark:focus:ring-yellow-700 group"
                  title="Bu seçenek seçilmesi halinde Ekip üyelerinin Kullanıcı rolleri'ne göre otomatik yetki seviyesi sistem tarafından belirlenir."
                >
                  <AlertCircle className="w-3 h-3" />
                  <span className="sr-only">Bilgi</span>
                  <div className="absolute hidden group-hover:block top-5 left-1/2 transform -translate-x-1/2 mt-1 w-64 p-2 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 text-xs text-gray-700 dark:text-gray-300 z-10 ml-24 mt-5">
                    Bu seçenek seçilmesi halinde Ekip üyelerinin Kullanıcı rolleri'ne göre otomatik yetki seviyesi sistem tarafından belirlenir.
                  </div>
                </button>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
                <span className="flex items-center gap-1.5">
                  {isFetching ? (
                    <span className="h-2 w-2 bg-gray-300 dark:bg-gray-600 rounded-full animate-pulse"></span>
                  ) : (
                    <span className="relative flex h-2 w-2">
                      <span className={localPermissions.stok_auto_permission ? "animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75" : ""}></span>
                      <span className={`relative inline-flex rounded-full h-2 w-2 ${localPermissions.stok_auto_permission ? "bg-yellow-500" : "bg-gray-400"}`}></span>
                    </span>
                  )}
                  <span>Otomatik Yetkilendirme {localPermissions.stok_auto_permission ? 'aktif' : 'pasif'}</span>
                </span>
              </div>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              className="sr-only peer" 
              checked={Boolean(localPermissions.stok_auto_permission)}
              onChange={() => handlePermissionChange('stok_auto_permission', !localPermissions.stok_auto_permission)}
              disabled={currentUserId === member.id || isFetching || userRole !== 'super'}
            />
            <div className={`w-11 h-6 rounded-full ${currentUserId === member.id || isFetching || userRole !== 'super' ? 'bg-gray-300 dark:bg-gray-600' : 'bg-gray-200 dark:bg-gray-700'} peer-focus:ring-2 peer-focus:ring-offset-2 peer-focus:ring-yellow-300 dark:peer-focus:ring-yellow-800 peer-checked:bg-gradient-to-r peer-checked:from-yellow-400 peer-checked:to-yellow-600 dark:peer-checked:from-yellow-500 dark:peer-checked:to-yellow-700 transition-all duration-300 ease-in-out after:content-[''] after:absolute after:bg-white after:border-0 after:rounded-full after:h-5 after:w-5 after:left-0.5 after:top-0.5 after:shadow-md after:transition-all after:duration-300 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full ${currentUserId === member.id || isFetching || userRole !== 'super' ? 'opacity-70 cursor-not-allowed' : ''}`}></div>
          </label>
        </div>
      </div>

      

      {/* Divider - with gradient */}
      <div className="mx-6 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent"></div>

      {/* Ürün Ekleme Yetkisi */}
      <div className="group transition-all duration-300 hover:bg-amber-50/30 dark:hover:bg-amber-900/10 rounded-[24px]">
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-900/20 dark:to-amber-900/10 text-amber-600 dark:text-amber-400 shadow-sm transform transition-transform duration-300 group-hover:scale-110">
              <Plus className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors duration-300">
                Ürün Ekleme Yetkisi
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
                {isFetching ? (
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2 bg-gray-300 dark:bg-gray-600 rounded-full animate-pulse"></span>
                    <span>Yükleniyor...</span>
                  </span>
                ) : localPermissions.stok_add_permission ? (
                  <>
                    <span className="flex items-center gap-1.5">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                      </span>
                      <span>Erişim yetkisi aktif</span>
                    </span>
                  </>
                ) : (
                  <>
                    <span className="flex items-center gap-1.5">
                      <span className="relative flex h-2 w-2">
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-gray-400"></span>
                      </span>
                      <span>Erişim yetkisi pasif</span>
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              className="sr-only peer" 
              checked={Boolean(localPermissions.stok_add_permission)}
              onChange={() => handlePermissionChange('stok_add_permission', !localPermissions.stok_add_permission)}
              disabled={currentUserId === member.id || isFetching || localPermissions.stok_auto_permission}
            />
            <div className={`w-11 h-6 rounded-full ${currentUserId === member.id || isFetching || localPermissions.stok_auto_permission ? 'bg-gray-300 dark:bg-gray-600' : 'bg-gray-200 dark:bg-gray-700'} peer-focus:ring-2 peer-focus:ring-offset-2 peer-focus:ring-amber-300 dark:peer-focus:ring-amber-800 peer-checked:bg-gradient-to-r peer-checked:from-amber-400 peer-checked:to-amber-600 dark:peer-checked:from-amber-500 dark:peer-checked:to-amber-700 transition-all duration-300 ease-in-out after:content-[''] after:absolute after:bg-white after:border-0 after:rounded-full after:h-5 after:w-5 after:left-0.5 after:top-0.5 after:shadow-md after:transition-all after:duration-300 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full ${currentUserId === member.id || isFetching || localPermissions.stok_auto_permission ? 'opacity-70 cursor-not-allowed' : ''}`}></div>
          </label>
        </div>
      </div>

      {/* Divider - with gradient */}
      <div className="mx-6 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent"></div>

      {/* Ürün Düzenleme Yetkisi */}
      <div className="group transition-all duration-300 hover:bg-orange-50/30 dark:hover:bg-orange-900/10 rounded-[24px]">
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-gradient-to-br from-orange-50 to-orange-100/50 dark:from-orange-900/20 dark:to-orange-900/10 text-orange-600 dark:text-orange-400 shadow-sm transform transition-transform duration-300 group-hover:scale-110">
              <Settings className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors duration-300">
                Ürün Düzenleme Yetkisi
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
                {isFetching ? (
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2 bg-gray-300 dark:bg-gray-600 rounded-full animate-pulse"></span>
                    <span>Yükleniyor...</span>
                  </span>
                ) : localPermissions.stok_edit_permission ? (
                  <>
                    <span className="flex items-center gap-1.5">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                      </span>
                      <span>Erişim yetkisi aktif</span>
                    </span>
                  </>
                ) : (
                  <>
                    <span className="flex items-center gap-1.5">
                      <span className="relative flex h-2 w-2">
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-gray-400"></span>
                      </span>
                      <span>Erişim yetkisi pasif</span>
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              className="sr-only peer" 
              checked={Boolean(localPermissions.stok_edit_permission)}
              onChange={() => handlePermissionChange('stok_edit_permission', !localPermissions.stok_edit_permission)}
              disabled={currentUserId === member.id || isFetching || localPermissions.stok_auto_permission}
            />
            <div className={`w-11 h-6 rounded-full ${currentUserId === member.id || isFetching || localPermissions.stok_auto_permission ? 'bg-gray-300 dark:bg-gray-600' : 'bg-gray-200 dark:bg-gray-700'} peer-focus:ring-2 peer-focus:ring-offset-2 peer-focus:ring-orange-300 dark:peer-focus:ring-orange-800 peer-checked:bg-gradient-to-r peer-checked:from-orange-400 peer-checked:to-orange-600 dark:peer-checked:from-orange-500 dark:peer-checked:to-orange-700 transition-all duration-300 ease-in-out after:content-[''] after:absolute after:bg-white after:border-0 after:rounded-full after:h-5 after:w-5 after:left-0.5 after:top-0.5 after:shadow-md after:transition-all after:duration-300 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full ${currentUserId === member.id || isFetching || localPermissions.stok_auto_permission ? 'opacity-70 cursor-not-allowed' : ''}`}></div>
          </label>
        </div>
      </div>

      {/* Divider - with gradient */}
      <div className="mx-6 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent"></div>

      {/* Stok Giriş/Çıkış Yetkisi */}
      <div className="group transition-all duration-300 hover:bg-red-50/30 dark:hover:bg-red-900/10  rounded-tr-[24px] rounded-tl-[24px]">
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-gradient-to-br from-red-50 to-red-100/50 dark:from-red-900/20 dark:to-red-900/10 text-red-600 dark:text-red-400 shadow-sm transform transition-transform duration-300 group-hover:scale-110">
              <RefreshCw className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors duration-300">
                Stok Giriş/Çıkış İşlemi Yapma Yetkisi
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
                {isFetching ? (
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2 bg-gray-300 dark:bg-gray-600 rounded-full animate-pulse"></span>
                    <span>Yükleniyor...</span>
                  </span>
                ) : localPermissions.stok_movement_permission ? (
                  <>
                    <span className="flex items-center gap-1.5">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                      </span>
                      <span>Erişim yetkisi aktif</span>
                    </span>
                  </>
                ) : (
                  <>
                    <span className="flex items-center gap-1.5">
                      <span className="relative flex h-2 w-2">
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-gray-400"></span>
                      </span>
                      <span>Erişim yetkisi pasif</span>
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              className="sr-only peer" 
              checked={Boolean(localPermissions.stok_movement_permission)}
              onChange={() => handlePermissionChange('stok_movement_permission', !localPermissions.stok_movement_permission)}
              disabled={currentUserId === member.id || isFetching || localPermissions.stok_auto_permission}
            />
            <div className={`w-11 h-6 rounded-full ${currentUserId === member.id || isFetching || localPermissions.stok_auto_permission ? 'bg-gray-300 dark:bg-gray-600' : 'bg-gray-200 dark:bg-gray-700'} peer-focus:ring-2 peer-focus:ring-offset-2 peer-focus:ring-red-300 dark:peer-focus:ring-red-800 peer-checked:bg-gradient-to-r peer-checked:from-red-400 peer-checked:to-red-600 dark:peer-checked:from-red-500 dark:peer-checked:to-red-700 transition-all duration-300 ease-in-out after:content-[''] after:absolute after:bg-white after:border-0 after:rounded-full after:h-5 after:w-5 after:left-0.5 after:top-0.5 after:shadow-md after:transition-all after:duration-300 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full ${currentUserId === member.id || isFetching || localPermissions.stok_auto_permission ? 'opacity-70 cursor-not-allowed' : ''}`}></div>
          </label>
        </div>
      </div>

      {/* Divider - with gradient */}
      <div className="mx-6 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent"></div>

      {/* Ürün Silme Yetkisi */}
      <div className="group transition-all duration-300 hover:bg-pink-50/30 dark:hover:bg-pink-900/10 rounded-[24px]">
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-gradient-to-br from-pink-50 to-pink-100/50 dark:from-pink-900/20 dark:to-pink-900/10 text-pink-600 dark:text-pink-400 shadow-sm transform transition-transform duration-300 group-hover:scale-110">
              <X className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-pink-600 dark:group-hover:text-pink-400 transition-colors duration-300">
                Ürün Silme Yetkisi
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
                {isFetching ? (
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2 bg-gray-300 dark:bg-gray-600 rounded-full animate-pulse"></span>
                    <span>Yükleniyor...</span>
                  </span>
                ) : localPermissions.stok_delete_permission ? (
                  <>
                    <span className="flex items-center gap-1.5">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                      </span>
                      <span>Erişim yetkisi aktif</span>
                    </span>
                  </>
                ) : (
                  <>
                    <span className="flex items-center gap-1.5">
                      <span className="relative flex h-2 w-2">
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-gray-400"></span>
                      </span>
                      <span>Erişim yetkisi pasif</span>
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              className="sr-only peer" 
              checked={Boolean(localPermissions.stok_delete_permission)}
              onChange={() => handlePermissionChange('stok_delete_permission', !localPermissions.stok_delete_permission)}
              disabled={currentUserId === member.id || isFetching || localPermissions.stok_auto_permission}
            />
            <div className={`w-11 h-6 rounded-full ${currentUserId === member.id || isFetching || localPermissions.stok_auto_permission ? 'bg-gray-300 dark:bg-gray-600' : 'bg-gray-200 dark:bg-gray-700'} peer-focus:ring-2 peer-focus:ring-offset-2 peer-focus:ring-pink-300 dark:peer-focus:ring-pink-800 peer-checked:bg-gradient-to-r peer-checked:from-pink-400 peer-checked:to-pink-600 dark:peer-checked:from-pink-500 dark:peer-checked:to-pink-700 transition-all duration-300 ease-in-out after:content-[''] after:absolute after:bg-white after:border-0 after:rounded-full after:h-5 after:w-5 after:left-0.5 after:top-0.5 after:shadow-md after:transition-all after:duration-300 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full ${currentUserId === member.id || isFetching || localPermissions.stok_auto_permission ? 'opacity-70 cursor-not-allowed' : ''}`}></div>
          </label>
        </div>
      </div>

      {/* Divider - with gradient */}
      <div className="mx-6 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent"></div>

      {/* Save Changes Button */}
      <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800/90 dark:to-gray-800/70 border-t border-gray-100 dark:border-gray-700/70">
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="p-1.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-500 dark:text-blue-400 mt-0.5">
              <AlertCircle className="w-3.5 h-3.5" />
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                {isFetching 
                  ? "Kullanıcı bilgileri yükleniyor, lütfen bekleyin..."
                  : "Kullanıcı yetkileri için yapılan değişiklikler \"Değişiklikleri Kaydet\" butonuna tıklayana kadar aktif olmaz."
                }
              </p>
            </div>
          </div>
          <button
            disabled={!hasChanges || isSaving || isFetching}
            onClick={saveChanges}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
              hasChanges && !isFetching
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            }`}
          >
            {isSaving ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Kaydediliyor...
              </>
            ) : isFetching ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Yükleniyor...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Değişiklikleri Kaydet
              </>
            )}
          </button>
        </div>
      </div>

      {/* Divider - with gradient */}
      <div className="mx-6 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent"></div>
    </>
  );
};

// Profile Details Modal Component for Super Users
const ProfileDetailsModal = ({ member, isOpen, onClose, onProfileUpdate }) => {
  const { isDark } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState(null);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [emailError, setEmailError] = useState('');
  const [tcError, setTcError] = useState('');

  // Format phone number function
  const formatPhoneNumber = (phone) => {
    if (!phone) return 'Belirtilmemiş';
    
    // Sadece rakamları al
    const cleaned = phone.replace(/\D/g, '');
    
    // Türkiye numarası kontrolü ve formatlama
    if (cleaned.startsWith('90') && cleaned.length === 12) {
      // 905551234567 -> +90 (555) 123 4567
      const countryCode = cleaned.substring(0, 2);
      const areaCode = cleaned.substring(2, 5);
      const firstPart = cleaned.substring(5, 8);
      const secondPart = cleaned.substring(8, 12);
      return `+${countryCode} (${areaCode}) ${firstPart} ${secondPart}`;
    } else if (cleaned.startsWith('5') && cleaned.length === 10) {
      // 5551234567 -> +90 (555) 123 4567
      const areaCode = cleaned.substring(0, 3);
      const firstPart = cleaned.substring(3, 6);
      const secondPart = cleaned.substring(6, 10);
      return `+90 (${areaCode}) ${firstPart} ${secondPart}`;
    } else if (cleaned.length === 11 && cleaned.startsWith('0')) {
      // 05551234567 -> +90 (555) 123 4567
      const areaCode = cleaned.substring(1, 4);
      const firstPart = cleaned.substring(4, 7);
      const secondPart = cleaned.substring(7, 11);
      return `+90 (${areaCode}) ${firstPart} ${secondPart}`;
    }
    
    // Diğer ülke kodları veya bilinmeyen formatlar için basit formatlama
    if (cleaned.length >= 10) {
      return `+${cleaned}`;
    }
    
    return phone; // Orijinal halini döndür
  };

  // Fetch complete profile data
  useEffect(() => {
    if (isOpen && member) {
      fetchProfileData();
    }
  }, [isOpen, member]);

  const fetchProfileData = async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', member.id)
        .single();
        
      if (error) throw error;
      
      setProfileData(data);
      setEditedProfile({
        ...data,
        birth_date: data.birth_date ? new Date(data.birth_date) : null,
      });
    } catch (error) {
      console.error('Error fetching profile data:', error);
      toast.error('Profil bilgileri alınamadı');
    } finally {
      setIsLoading(false);
    }
  };

  // Email validation function
  const validateEmailFormat = (email) => {
    if (!email) return false;
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return emailRegex.test(email);
  };

  // TC Kimlik validation
  const validateTCNo = (tcno) => {
    if (!tcno) return true; // Allow empty
    if (tcno.length !== 11) return false;
    if (tcno[0] === '0') return false;
    if (!/^\d+$/.test(tcno)) return false;

    let oddSum = 0;
    let evenSum = 0;
    let total = 0;

    for (let i = 0; i < 9; i++) {
      const digit = parseInt(tcno[i]);
      if (i % 2 === 0) {
        oddSum += digit;
      } else {
        evenSum += digit;
      }
      total += digit;
    }

    if ((oddSum * 7 - evenSum) % 10 !== parseInt(tcno[9])) return false;
    if ((total + parseInt(tcno[9])) % 10 !== parseInt(tcno[10])) return false;

    return true;
  };

  // Handle saving profile changes
  const handleSaveChanges = async () => {
    // Validation
    if (!editedProfile.first_name.trim()) {
      toast.error('Ad alanı boş olamaz');
      return;
    }
    if (!editedProfile.last_name.trim()) {
      toast.error('Soyad alanı boş olamaz');
      return;
    }

    // Email validation
    if (!validateEmailFormat(editedProfile.email)) {
      setIsEmailValid(false);
      setEmailError('Geçerli bir e-posta adresi giriniz');
      toast.error('E-posta hatalı');
      return;
    } else {
      setIsEmailValid(true);
      setEmailError('');
    }

    // TC Kimlik validation
    if (editedProfile.national_id && !validateTCNo(editedProfile.national_id)) {
      setTcError('TC Kimlik Numaranız geçersiz');
      toast.error('TC Kimlik Numaranız geçersiz');
      return;
    } else {
      setTcError('');
    }

    try {
      setIsLoading(true);
      
      const birthDateValue = editedProfile.birth_date
        ? editedProfile.birth_date.toISOString()
        : null;

      const { error } = await supabase
        .from('user_profiles')
        .update({
          first_name: editedProfile.first_name,
          last_name: editedProfile.last_name,
          email: editedProfile.email,
          phone: editedProfile.phone || '',
          job_position: editedProfile.job_position || '',
          website: editedProfile.website || '',
          address: editedProfile.address || '',
          country: editedProfile.country || '',
          city: editedProfile.city || '',
          birth_date: birthDateValue,
          gender: editedProfile.gender || '',
          national_id: editedProfile.national_id || '',
          postal_code: editedProfile.postal_code || '',
          updated_at: new Date().toISOString(),
        })
        .eq('id', member.id);

      if (error) throw error;

      // Update local data
      setProfileData({
        ...editedProfile,
        birth_date: birthDateValue,
      });
      
      setIsEditing(false);
      toast.success('Profil başarıyla güncellendi');
      
      // Refresh team members to reflect changes in parent
      if (onProfileUpdate) {
        onProfileUpdate();
      }
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error('Profil güncellenirken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  // Format name functions
  const formatFirstName = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0).toLocaleUpperCase('tr-TR') + word.slice(1).toLocaleLowerCase('tr-TR'))
      .join(' ');
  };

  const formatLastName = (name) => {
    return name.toLocaleUpperCase('tr-TR');
  };

  // Calculate account age
  const calculateAccountAge = (createdAt) => {
    if (!createdAt) return 'Bilinmiyor';
    
    const now = new Date();
    const created = new Date(createdAt);
    const diffTime = Math.abs(now - created);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) {
      return `${diffDays} gün`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} ay`;
    } else {
      const years = Math.floor(diffDays / 365);
      return `${years} yıl`;
    }
  };
  
  // If the modal is closed or there's no member, don't render anything
  if (!isOpen || !member) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className={`w-full max-w-4xl max-h-[90vh] overflow-y-auto ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-xl border`}
      >
        {/* Header */}
        <div className={`sticky top-0 z-10 ${isDark ? 'bg-gray-800/80 border-gray-700' : 'bg-white/80 border-gray-200'} backdrop-blur-xl border-b px-6 py-4 flex items-center justify-between`}>
          <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Profil Detayları - {member.name}
          </h2>
          <div className="flex items-center gap-4">
            {isEditing ? (
              <button
                onClick={() => {
                  setEditedProfile({
                    ...profileData,
                    birth_date: profileData.birth_date ? new Date(profileData.birth_date) : null,
                  });
                  setIsEditing(false);
                  setEmailError('');
                  setIsEmailValid(true);
                  setTcError('');
                }}
                className={`
                  p-2 rounded-full
                  ${
                    isDark
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }
                  transition-colors
                `}
              >
                <X className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className={`
                  p-2 rounded-full
                  ${
                    isDark
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }
                  transition-colors
                `}
              >
                <Edit2 className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className={`
                p-2 rounded-full
                ${
                  isDark
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }
                transition-colors
              `}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Avatar and basic info */}
              <div className="flex items-center gap-6 mb-8">
                <img
                  src={profileData?.profile_photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(
                      profileData?.first_name + ' ' + profileData?.last_name
                  )}&background=3b82f6&color=fff`}
                  alt={`${profileData?.first_name} ${profileData?.last_name}`}
                  className="w-24 h-24 rounded-full object-cover border-4 border-primary-500"
                />
                <div>
                  <h3 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {profileData?.first_name} {profileData?.last_name}
                  </h3>
                  <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                    {profileData?.job_position || 'Pozisyon belirtilmemiş'}
                  </p>
                  <div className={`inline-flex mt-2 px-3 py-1 rounded-full text-xs ${
                    profileData?.role === 'super' 
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' 
                      : profileData?.role === 'admin'
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                        : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                  }`}>
                    {profileData?.role === 'super' ? 'Süper Yönetici' : profileData?.role === 'admin' ? 'Admin' : 'Kullanıcı'}
                  </div>
                </div>
              </div>
              
              {/* Stats / Yetki & Doğum Tarihi */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                {/* Yetki Kartı */}
                <div className={`p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50 flex items-center gap-3`}>
                  <div className="p-2 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400">
                    <Shield className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Yetki</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {profileData?.role === 'super' ? 'Süper Yönetici' : profileData?.role === 'admin' ? 'Admin' : 'Kullanıcı'}
                    </p>
                  </div>
                </div>
                
                {/* Doğum Tarihi Kartı - Input Field */}
                <div className={`p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50`}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-1.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400">
                      <Calendar className="w-4 h-4" />
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Doğum Tarihi</p>
                  </div>
                  <div className="relative">
                    {isEditing ? (
                      <input
                        type="date"
                        value={editedProfile?.birth_date ? new Date(editedProfile.birth_date).toISOString().substring(0,10) : ''}
                        onChange={(e) => setEditedProfile({...editedProfile, birth_date: e.target.value ? new Date(e.target.value) : null})}
                        className={`w-full px-3 py-2 text-sm rounded-lg border ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                      />
                    ) : (
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {profileData?.birth_date ? new Date(profileData.birth_date).toLocaleDateString('tr-TR') : 'Belirtilmemiş'}
                      </p>
                    )}
                  </div>
                </div>

                {/* Hesap Oluşturulma Tarihi Kartı */}
                <div className={`p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50 flex items-center gap-3 md:col-span-2 lg:col-span-1`}>
                  <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400">
                    <UserPlus className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Hesap Oluşturulma</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                      {profileData?.created_at ? new Date(profileData.created_at).toLocaleDateString('tr-TR') : 'Bilinmiyor'}
                    </p>
                    <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                      {calculateAccountAge(profileData?.created_at)} önce
                    </p>
                  </div>
                </div>
              </div>

              {/* Address Section - Full Width */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Adres Bilgileri
                  </h3>
                </div>
                <div className="relative">
                  <MapPin className="absolute left-4 top-4 w-5 h-5 text-gray-400 dark:text-gray-500 z-10" />
                  <textarea
                    value={editedProfile?.address || ''}
                    onChange={(e) => setEditedProfile({...editedProfile, address: e.target.value})}
                    disabled={!isEditing}
                    rows={3}
                    className={`
                      w-full pl-12 pr-4 py-4 rounded-xl border
                      min-h-[100px] max-h-[300px] resize-y
                      ${
                        isDark
                          ? 'bg-white/5 border-white/10 text-white placeholder-gray-400'
                          : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                      }
                      disabled:opacity-60 disabled:cursor-not-allowed
                      focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                      transition-all duration-200
                    `}
                    placeholder="Detaylı adres bilgilerini giriniz..."
                  />
                  <div className="absolute bottom-2 right-2 text-xs text-gray-400 dark:text-gray-500 pointer-events-none">
                    Genişletilebilir
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Personal Information */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                    <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Kişisel Bilgiler
                    </h3>
                  </div>

                  {/* First Name - Last Name */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Ad
                      </label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                        <input
                          type="text"
                          value={editedProfile?.first_name || ''}
                          onChange={(e) => setEditedProfile({...editedProfile, first_name: formatFirstName(e.target.value)})}
                          disabled={!isEditing}
                          className={`
                            w-full pl-10 pr-4 py-3 rounded-xl border
                            ${
                              isDark
                                ? 'bg-white/5 border-white/10 text-white placeholder-gray-400'
                                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                            }
                            disabled:opacity-60 disabled:cursor-not-allowed
                          `}
                          placeholder="Ad"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Soyad
                      </label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                        <input
                          type="text"
                          value={editedProfile?.last_name || ''}
                          onChange={(e) => setEditedProfile({...editedProfile, last_name: formatLastName(e.target.value)})}
                          disabled={!isEditing}
                          className={`
                            w-full pl-10 pr-4 py-3 rounded-xl border
                            ${
                              isDark
                                ? 'bg-white/5 border-white/10 text-white placeholder-gray-400'
                                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                            }
                            disabled:opacity-60 disabled:cursor-not-allowed
                          `}
                          placeholder="Soyad"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      E-posta
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                      <input
                        type="email"
                        value={editedProfile?.email || ''}
                        onChange={(e) => setEditedProfile({...editedProfile, email: e.target.value})}
                        disabled={!isEditing}
                        className={`
                          w-full pl-10 pr-4 py-3 rounded-xl border
                          ${
                            !isEmailValid && isEditing
                              ? 'border-red-500'
                              : isDark
                              ? 'bg-white/5 border-white/10 text-white placeholder-gray-400'
                              : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                          }
                          disabled:opacity-60 disabled:cursor-not-allowed
                        `}
                        placeholder="E-posta"
                      />
                      {emailError && !isEmailValid && isEditing && (
                        <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {emailError}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Phone */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Telefon
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                      {!isEditing ? (
                        <input
                          type="text"
                          disabled
                          value={formatPhoneNumber(editedProfile?.phone)}
                          className={`
                            w-full pl-10 pr-4 py-3 rounded-xl border
                            ${
                              isDark
                                ? 'bg-white/5 border-white/10 text-white placeholder-gray-400'
                                : 'bg-gray-100 border-gray-200 text-gray-900 placeholder-gray-400'
                            }
                            disabled:opacity-60
                          `}
                        />
                      ) : (
                        <input
                          type="text"
                          value={editedProfile?.phone || ''}
                          onChange={(e) => {
                            // Format phone number as "+90 (XXX) XXX XXXX"
                            let value = e.target.value.replace(/\D/g, '');
                            if (value.startsWith('90')) {
                              value = value.substring(2);
                            }
                            if (value.length > 0) {
                              if (value.length <= 3) {
                                value = `+90 (${value}`;
                              } else if (value.length <= 6) {
                                value = `+90 (${value.substring(0, 3)}) ${value.substring(3)}`;
                              } else {
                                value = `+90 (${value.substring(0, 3)}) ${value.substring(3, 6)} ${value.substring(6, 10)}`;
                              }
                            }
                            setEditedProfile({...editedProfile, phone: value});
                          }}
                          className={`
                            w-full pl-10 pr-4 py-3 rounded-xl border
                            ${
                              isDark
                                ? 'bg-white/5 border-white/10 text-white placeholder-gray-400'
                                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                            }
                          `}
                          placeholder="+90 (5XX) XXX XXXX"
                          maxLength={18}
                        />
                      )}
                    </div>
                  </div>
                  
                  {/* City and Country */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Şehir
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                        <input
                          type="text"
                          value={editedProfile?.city || ''}
                          onChange={(e) => setEditedProfile({...editedProfile, city: e.target.value})}
                          disabled={!isEditing}
                          className={`
                            w-full pl-10 pr-4 py-3 rounded-xl border
                            ${
                              isDark
                                ? 'bg-white/5 border-white/10 text-white placeholder-gray-400'
                                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                            }
                            disabled:opacity-60 disabled:cursor-not-allowed
                          `}
                          placeholder="Şehir"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Ülke
                      </label>
                      <div className="relative">
                        <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                        <input
                          type="text"
                          value={editedProfile?.country || ''}
                          onChange={(e) => setEditedProfile({...editedProfile, country: e.target.value})}
                          disabled={!isEditing}
                          className={`
                            w-full pl-10 pr-4 py-3 rounded-xl border
                            ${
                              isDark
                                ? 'bg-white/5 border-white/10 text-white placeholder-gray-400'
                                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                            }
                            disabled:opacity-60 disabled:cursor-not-allowed
                          `}
                          placeholder="Ülke"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Doğum Tarihi */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Doğum Tarihi
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                      {isEditing ? (
                        <input
                          type="date"
                          value={editedProfile?.birth_date ? new Date(editedProfile.birth_date).toISOString().substring(0,10) : ''}
                          onChange={(e) => setEditedProfile({...editedProfile, birth_date: e.target.value ? new Date(e.target.value) : null})}
                          className={`
                            w-full pl-10 pr-4 py-3 rounded-xl border
                            ${
                              isDark
                                ? 'bg-white/5 border-white/10 text-white placeholder-gray-400'
                                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                            }
                          `}
                        />
                      ) : (
                        <input
                          type="text"
                          disabled
                          value={editedProfile?.birth_date ? new Date(editedProfile.birth_date).toLocaleDateString('tr-TR') : 'Belirtilmemiş'}
                          className={`
                            w-full pl-10 pr-4 py-3 rounded-xl border
                            ${
                              isDark
                                ? 'bg-white/5 border-white/10 text-white placeholder-gray-400'
                                : 'bg-gray-100 border-gray-200 text-gray-900 placeholder-gray-400'
                            }
                            disabled:opacity-60
                          `}
                        />
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Other Information */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                    <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Diğer Bilgiler
                    </h3>
                  </div>
                  
                  {/* Job Position */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Pozisyon
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                      <input
                        type="text"
                        value={editedProfile?.job_position || ''}
                        onChange={(e) => setEditedProfile({...editedProfile, job_position: e.target.value})}
                        disabled={!isEditing}
                        className={`
                          w-full pl-10 pr-4 py-3 rounded-xl border
                          ${
                            isDark
                              ? 'bg-white/5 border-white/10 text-white placeholder-gray-400'
                              : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                          }
                          disabled:opacity-60 disabled:cursor-not-allowed
                        `}
                        placeholder="Pozisyon"
                      />
                    </div>
                  </div>
                  
                  {/* Website */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Website
                    </label>
                    <div className="relative">
                      <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                      <input
                        type="text"
                        value={editedProfile?.website || ''}
                        onChange={(e) => setEditedProfile({...editedProfile, website: e.target.value})}
                        disabled={!isEditing}
                        className={`
                          w-full pl-10 pr-4 py-3 rounded-xl border
                          ${
                            isDark
                              ? 'bg-white/5 border-white/10 text-white placeholder-gray-400'
                              : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                          }
                          disabled:opacity-60 disabled:cursor-not-allowed
                        `}
                        placeholder="Website"
                      />
                    </div>
                  </div>
                  
                  {/* TC ID Number */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      TC Kimlik No
                    </label>
                    <div className="relative">
                      <Hash className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                      <input
                        type="text"
                        value={editedProfile?.national_id || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          if ((/^\d+$/.test(val) && val.length <= 11) || val === '') {
                            setEditedProfile({...editedProfile, national_id: val});
                            if (val.length === 11 && !validateTCNo(val)) {
                              setTcError('Geçersiz TC Kimlik Numarası');
                            } else {
                              setTcError('');
                            }
                          }
                        }}
                        disabled={!isEditing}
                        className={`
                          w-full pl-10 pr-4 py-3 rounded-xl border
                          ${
                            tcError && isEditing
                              ? 'border-red-500'
                              : isDark
                              ? 'bg-white/5 border-white/10 text-white placeholder-gray-400'
                              : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                          }
                          disabled:opacity-60 disabled:cursor-not-allowed
                        `}
                        placeholder="11 haneli TC Kimlik No"
                      />
                      {tcError && isEditing && (
                        <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {tcError}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Postal Code */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Posta Kodu
                    </label>
                    <div className="relative">
                      <Flag className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                      <input
                        type="text"
                        value={editedProfile?.postal_code || ''}
                        onChange={(e) => setEditedProfile({...editedProfile, postal_code: e.target.value})}
                        disabled={!isEditing}
                        className={`
                          w-full pl-10 pr-4 py-3 rounded-xl border
                          ${
                            isDark
                              ? 'bg-white/5 border-white/10 text-white placeholder-gray-400'
                              : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                          }
                          disabled:opacity-60 disabled:cursor-not-allowed
                        `}
                        placeholder="Posta Kodu"
                      />
                    </div>
                  </div>
                  
                  {/* Gender */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Cinsiyet
                    </label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                      <select
                        value={editedProfile?.gender || ''}
                        onChange={(e) => setEditedProfile({...editedProfile, gender: e.target.value})}
                        disabled={!isEditing}
                        className={`
                          w-full pl-10 pr-4 py-3 rounded-xl border appearance-none
                          ${
                            isDark
                              ? 'bg-white/5 border-white/10 text-white placeholder-gray-400'
                              : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                          }
                          disabled:opacity-60 disabled:cursor-not-allowed
                        `}
                      >
                        <option value="">Seçiniz</option>
                        <option value="male">Erkek</option>
                        <option value="female">Kadın</option>
                        <option value="other">Diğer</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Save Button (only shown when editing) */}
              {isEditing && (
                <div className="flex justify-end mt-8">
                  <button
                    onClick={handleSaveChanges}
                    disabled={isLoading}
                    className="px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Kaydediliyor...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        Değişiklikleri Kaydet
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const AddMemberModal = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
    onClick={() => setIsAddModalOpen(false)}
  >
    <motion.div
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.95, opacity: 0 }}
      onClick={(e) => e.stopPropagation()}
      className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-800 rounded-xl shadow-xl"
    >
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Yeni Ekip Üyesi Ekle
          </h2>
          <button
            type="button"
            onClick={() => setIsAddModalOpen(false)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
      </div>

      <form className="p-6 space-y-6 ">
        {/* Profil Fotoğrafı */}
        <div className="flex flex-col items-center">
          <div className="relative">
            <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
              <Upload className="w-8 h-8 text-gray-400" />
            </div>
            <button
              type="button"
              className="absolute bottom-0 right-0 p-2 bg-primary-600 rounded-full text-white hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Profil fotoğrafı ekleyin
          </p>
        </div>

        {/* Kişisel Bilgiler */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ad Soyad
            </label>
            <input
              type="text"
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
              placeholder="Ad Soyad giriniz"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              E-posta
            </label>
            <input
              type="email"
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
              placeholder="E-posta adresi giriniz"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Telefon
            </label>
            <input
              type="tel"
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
              placeholder="Telefon numarası giriniz"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Konum
            </label>
            <input
              type="text"
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
              placeholder="Şehir, Ülke"
            />
          </div>
        </div>

        {/* Yetenekler */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Yetenekler
          </label>
          <input
            type="text"
            className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
            placeholder="Yetenekleri virgülle ayırarak girin"
          />
        </div>

        {/* Biyografi */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Biyografi
          </label>
          <textarea
            rows={4}
            className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors resize-none"
            placeholder="Kısa bir biyografi yazın..."
          />
        </div>
      </form>

      <div className="p-6 border-t border-gray-200 dark:border-gray-700">
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setIsAddModalOpen(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            İptal
          </button>
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            Üye Ekle
          </button>
        </div>
      </div>
    </motion.div>
  </motion.div>
);